/* PRODUCCIÓN — Órdenes de Trabajo (OT) page
   Soluciones Arquitectónicas — module gated to that business.
*/
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Hammer, Plus, FileText, Warning, CheckCircle, Clock, CurrencyDollar,
  Package, Users as UsersIcon, Receipt, ChartLine, Download, ArrowsClockwise, X, Trash, PaperclipHorizontal,
  Buildings, MagnifyingGlass, LinkSimple,
} from '@phosphor-icons/react';
import api from '@/api/axios';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const SOLAR_ID = '8353ba2a-6906-4faa-93ee-e5c03bf0daa5'; // Soluciones Arquitectonicas
const ESTADOS = [
  { value: 'abierta', label: 'Abierta', color: 'bg-blue-500/15 border-blue-500/40 text-blue-700 dark:text-blue-300' },
  { value: 'en_proceso', label: 'En proceso', color: 'bg-amber-500/15 border-amber-500/40 text-amber-800 dark:text-amber-300' },
  { value: 'instalando', label: 'Instalando', color: 'bg-purple-500/15 border-purple-500/40 text-purple-700 dark:text-purple-300' },
  { value: 'entregada', label: 'Entregada', color: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-700 dark:text-emerald-300' },
  { value: 'cerrada', label: 'Cerrada', color: 'bg-slate-500/15 border-slate-500/40 text-slate-700 dark:text-slate-300' },
  { value: 'cancelada', label: 'Cancelada', color: 'bg-red-500/15 border-red-500/40 text-red-700 dark:text-red-300' },
];

const fmtQ = (n) => `Q ${Number(n || 0).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('es-GT') : '—';

const Produccion = () => {
  const { selectedBusiness } = useOutletContext();
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase();
  const businessId = selectedBusiness?.id || SOLAR_ID;
  const isSolarBiz = (selectedBusiness?.name || '').toLowerCase().includes('soluciones arquitec');

  const [ots, setOts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [openOtId, setOpenOtId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('produccion_view') || 'cards'); // 'cards' | 'table'
  const [alerts, setAlerts] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/produccion/ots?business_id=${businessId}&limit=300`);
      setOts(res.data?.ots || []);
      const al = await api.get(`/produccion/alerts?business_id=${businessId}`);
      setAlerts(al.data?.alerts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error cargando OTs');
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const g = {};
    ESTADOS.forEach((e) => { g[e.value] = []; });
    ots.forEach((o) => { (g[o.estado] || g.abierta).push(o); });
    return g;
  }, [ots]);

  if (!isSolarBiz) {
    return (
      <div className="p-6 max-w-3xl mx-auto" data-testid="page-produccion-no-access">
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-[14px] p-8 text-center">
          <Warning size={48} weight="duotone" className="mx-auto text-amber-600 dark:text-amber-400 mb-3" />
          <h2 className="text-lg font-black text-[var(--text)] mb-1">Módulo solo para Soluciones Arquitectónicas</h2>
          <p className="text-sm text-[var(--text-muted)]">
            Cambia al negocio &quot;Soluciones Arquitectónicas&quot; en el selector superior para acceder al módulo PRODUCCIÓN.
          </p>
        </div>
      </div>
    );
  }

  const filtered = filter === 'all' ? ots : ots.filter(o => o.estado === filter);

  const exportBatch = async () => {
    try {
      const filtered = grouped[filter] || ots;
      if (!filtered || filtered.length === 0) {
        toast.error('No hay OTs que coincidan con el filtro actual');
        return;
      }
      if (filtered.length > 50) {
        toast.error('Máx. 50 OTs por lote — refina el filtro');
        return;
      }
      const ids = filtered.map(o => o.id).join(',');
      const url = `/produccion/ot/export/pdf-form-batch?business_id=${businessId}&ids=${ids}`;
      const res = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `OTs_formulario_lote_${filtered.length}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
      toast.success(`PDF generado con ${filtered.length} OT(s)`);
    } catch (e) {
      toast.error('Error generando lote: ' + (e?.response?.data?.detail || e?.message || 'desconocido'));
    }
  };

  const exportLista = async () => {
    try {
      const res = await api.get(`/produccion/ots/reporte-lista/pdf?business_id=${businessId}`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = 'OTs_lista_semaforo.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
      toast.success('Listado con semáforo descargado');
    } catch (e) {
      let msg = 'Error generando listado';
      try {
        if (e?.response?.data instanceof Blob) {
          const txt = JSON.parse(await e.response.data.text());
          msg = txt.detail || msg;
        } else {
          msg = e?.response?.data?.detail || msg;
        }
      } catch { /* noop */ }
      toast.error(msg);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto" data-testid="page-produccion">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shrink-0">
            <Hammer size={26} weight="fill" className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-[var(--text)] heading-font">Producción · Órdenes de Trabajo</h1>
            <p className="text-sm text-[var(--text-muted)] mt-0.5">
              {ots.length} OT · <span className="font-bold">{selectedBusiness?.name || ''}</span> {alerts.length > 0 && <span className="text-red-400 font-bold">· {alerts.length} alerta(s)</span>}
            </p>
            {ots.length === 0 && !loading && (
              <p className="text-[11px] text-amber-600 dark:text-amber-400 font-semibold mt-0.5" data-testid="produccion-empty-hint">
                Este negocio no tiene OTs. Si esperabas verlas, revisa el selector de negocio (ej. "Soluciones Arquitectonicas" vs "de Honduras").
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="px-3 py-2 border border-[var(--border)] rounded-[10px] text-sm font-bold hover:bg-[var(--surface-muted)] flex items-center gap-1.5" data-testid="btn-refresh">
            <ArrowsClockwise size={14} weight="bold" /> Actualizar
          </button>
          <button onClick={exportLista} title="Listado de OTs en curso con semáforo de fecha ofrecida" className="px-3 py-2 border border-emerald-500/40 text-emerald-500 rounded-[10px] text-sm font-bold hover:bg-emerald-500/10 flex items-center gap-1.5" data-testid="btn-export-lista">
            🚦 Lista semáforo
          </button>
          <button onClick={exportBatch} title="Imprimir formularios de las OTs del filtro actual (lote)" className="px-3 py-2 border border-orange-500/40 text-orange-300 rounded-[10px] text-sm font-bold hover:bg-orange-500/10 flex items-center gap-1.5" data-testid="btn-export-batch">
            📋 Imprimir lote
          </button>
          <button onClick={() => setShowCreate(true)} className="px-4 py-2.5 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-[10px] font-bold text-sm flex items-center gap-2" data-testid="btn-new-ot">
            <Plus size={16} weight="bold" /> Nueva OT
          </button>
        </div>
      </div>

      {/* Alertas */}
      {alerts.length > 0 && (
        <div className="mb-4 bg-red-500/10 border border-red-500/30 rounded-[12px] p-3 max-h-40 overflow-y-auto" data-testid="alerts-panel">
          <p className="text-xs font-black uppercase text-red-300 mb-1.5">🚨 Alertas activas</p>
          {alerts.slice(0, 6).map((a, i) => (
            <button key={i} onClick={() => setOpenOtId(a.ot_id)} className="block w-full text-left text-xs text-red-200 hover:text-white py-0.5">
              • <b>{a.title}</b> — {a.message}
            </button>
          ))}
        </div>
      )}

      {/* Filtros */}
      <div className="flex flex-wrap gap-1.5 mb-4 items-center">
        <button onClick={() => setFilter('all')} className={`px-3 py-1 text-xs font-bold rounded-full border ${filter === 'all' ? 'bg-[var(--surface-muted)] border-[var(--text-muted)]' : 'border-[var(--border)]'}`}>Todas ({ots.length})</button>
        {ESTADOS.map(e => (
          <button key={e.value} onClick={() => setFilter(e.value)} className={`px-3 py-1 text-xs font-bold rounded-full border ${filter === e.value ? e.color : 'border-[var(--border)] text-[var(--text-muted)]'}`} data-testid={`filter-${e.value}`}>
            {e.label} ({grouped[e.value]?.length || 0})
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1 bg-[var(--surface-muted)] rounded-full p-0.5" data-testid="view-mode-toggle">
          <button onClick={() => { setViewMode('cards'); localStorage.setItem('produccion_view', 'cards'); }} className={`px-3 py-1 text-xs font-bold rounded-full ${viewMode === 'cards' ? 'bg-[var(--surface)] shadow-sm text-[var(--text)]' : 'text-[var(--text-muted)]'}`} data-testid="btn-view-cards">Cards</button>
          <button onClick={() => { setViewMode('table'); localStorage.setItem('produccion_view', 'table'); }} className={`px-3 py-1 text-xs font-bold rounded-full ${viewMode === 'table' ? 'bg-[var(--surface)] shadow-sm text-[var(--text)]' : 'text-[var(--text-muted)]'}`} data-testid="btn-view-table">Tabla</button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-[var(--text-muted)]">Cargando…</p>
      ) : ots.length === 0 ? (
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-10 text-center" data-testid="empty-ots">
          <Hammer size={48} weight="duotone" className="mx-auto text-orange-400 mb-3" />
          <p className="text-base font-bold text-[var(--text)]">Sin OTs aún</p>
          <p className="text-sm text-[var(--text-muted)] mt-1">Crea la primera al registrar un anticipo.</p>
        </div>
      ) : viewMode === 'cards' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map((o) => {
            const est = ESTADOS.find(e => e.value === o.estado) || ESTADOS[0];
            return (
              <button key={o.id} onClick={() => setOpenOtId(o.id)} className={`text-left bg-[var(--surface)] border border-[var(--border)] hover:border-orange-500/40 rounded-[12px] p-4 transition-colors ${o.estado === 'cancelada' ? 'opacity-60 grayscale border-red-500/40' : ''}`} data-testid={`ot-card-${o.id}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className={`text-[10px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full border ${est.color}`}>{est.label}</span>
                  <span className="text-[10px] text-[var(--text-faint)]">{fmtDate(o.created_at)}</span>
                </div>
                <p className={`text-sm font-black mb-0.5 ${o.estado === 'cancelada' ? 'line-through text-red-600' : 'text-[var(--text)]'}`}>
                  {o.numero}
                  {o.es_suministro && <span className="ml-1.5 text-[8px] px-1 py-0.5 bg-blue-500/20 text-blue-700 dark:text-blue-300 rounded font-bold">SUM</span>}
                </p>
                <p className="text-xs text-[var(--text-muted)]">{o.cliente?.nombre || '—'}</p>
                <p className="text-xs text-[var(--text-faint)] mt-1 truncate">📍 {o.direccion_proyecto || '—'}</p>
                <div className="flex justify-between items-center mt-2 pt-2 border-t border-[var(--border)]">
                  <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">{fmtQ(o.monto_total_cotizado)}</span>
                  <span className="text-[10px] text-[var(--text-faint)]">{o.vendor_name}</span>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[12px] overflow-x-auto" data-testid="ot-table-view">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase font-bold text-[var(--text-muted)] border-b border-[var(--border)]">
                <th className="text-left py-2 px-3">N° OT</th>
                <th className="text-left py-2 px-2">Cliente</th>
                <th className="text-left py-2 px-2">Descripción</th>
                <th className="text-right py-2 px-2">Valor total</th>
                <th className="text-left py-2 px-2">Etapa</th>
                <th className="text-left py-2 px-2">Instalador</th>
                <th className="text-left py-2 px-3">Vendedor</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => {
                const est = ESTADOS.find(e => e.value === o.estado) || ESTADOS[0];
                return (
                  <tr key={o.id} onClick={() => setOpenOtId(o.id)} className={`border-b border-[var(--border)]/40 hover:bg-[var(--surface-muted)] cursor-pointer ${o.estado === 'cancelada' ? 'opacity-60' : ''}`} data-testid={`ot-row-${o.id}`}>
                    <td className={`py-2 px-3 font-mono font-black ${o.estado === 'cancelada' ? 'line-through text-red-600' : 'text-[var(--text)]'}`}>
                      {o.numero}
                      {o.es_suministro && <span className="ml-1 text-[8px] px-1 py-0.5 bg-blue-500/20 text-blue-700 dark:text-blue-300 rounded">SUM</span>}
                    </td>
                    <td className="py-2 px-2 text-[var(--text)] truncate max-w-[180px]" title={o.cliente?.nombre}>{o.cliente?.nombre || '—'}</td>
                    <td className="py-2 px-2 text-[var(--text-muted)] truncate max-w-[280px]" title={o.descripcion}>{o.descripcion || o.direccion_proyecto || '—'}</td>
                    <td className="py-2 px-2 text-right font-bold text-emerald-600 dark:text-emerald-400 whitespace-nowrap">{fmtQ(o.monto_total_cotizado)}</td>
                    <td className="py-2 px-2"><span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full border ${est.color}`}>{est.label}</span></td>
                    <td className="py-2 px-2 text-[var(--text-muted)] truncate max-w-[140px]" title={o.responsable_produccion_nombre}>{o.responsable_produccion_nombre || '—'}</td>
                    <td className="py-2 px-3 text-[var(--text-muted)] truncate max-w-[120px]">{o.vendor_name || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="text-[10px] text-[var(--text-muted)] p-2">{filtered.length} OT(s) · clic en la fila para abrir</p>
        </div>
      )}

      {showCreate && <CreateOTModal businessId={businessId} onClose={() => setShowCreate(false)} onCreated={(id) => { setShowCreate(false); load(); setOpenOtId(id); }} />}
      {openOtId && <OTDetailModal otId={openOtId} role={role} onClose={() => setOpenOtId(null)} onChanged={load} />}
    </div>
  );
};


// ─────────────────────── Create OT Modal ───────────────────────
const CreateOTModal = ({ businessId, onClose, onCreated }) => {
  const [step, setStep] = useState(1);
  const [catalog, setCatalog] = useState({ tipos_proyecto: [] });
  const [nextNum, setNextNum] = useState('');
  const [numero, setNumero] = useState('');
  const [cliente, setCliente] = useState({ nombre: '', telefono: '', email: '', nit: '', direccion_facturacion: '' });
  const [tipos, setTipos] = useState([]);
  const [tiposExtra, setTiposExtra] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [direccion, setDireccion] = useState('');
  const [numCot, setNumCot] = useState('');
  const [fechaIni, setFechaIni] = useState('');
  const [fechaEnt, setFechaEnt] = useState('');
  const [montoCot, setMontoCot] = useState('');
  const [anticipo, setAnticipo] = useState({ monto: '', fecha: '', url: '', nombre: '' });
  const [bypassPin, setBypassPin] = useState('');
  const [bypassMotivo, setBypassMotivo] = useState('');
  const [esSuministro, setEsSuministro] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Hoy en formato ISO — usado como `min` en los date pickers para bloquear fechas retroactivas
  const todayISO = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    (async () => {
      try {
        const [c, n] = await Promise.all([
          api.get('/produccion/catalog'),
          api.get('/produccion/next-number'),
        ]);
        setCatalog(c.data);
        setNextNum(n.data?.numero || '');
        setNumero(n.data?.numero || '');
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const toggleTipo = (v) => setTipos(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);

  const uploadFile = async (file) => {
    if (!file) return null;
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/produccion/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      return r.data;
    } catch (e) {
      toast.error('Error subiendo archivo');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!cliente.nombre) return toast.error('Nombre del cliente requerido');
    try {
      setSubmitting(true);
      // Validación cliente-side: bloquear fechas retroactivas antes de enviar
      if (fechaIni && fechaIni < todayISO) {
        toast.error('La fecha de inicio no puede ser retroactiva');
        setSubmitting(false); return;
      }
      if (fechaEnt && fechaEnt < todayISO) {
        toast.error('La fecha de entrega no puede ser retroactiva');
        setSubmitting(false); return;
      }
      if (fechaIni && fechaEnt && fechaEnt < fechaIni) {
        toast.error('La fecha de entrega no puede ser antes de la fecha de inicio');
        setSubmitting(false); return;
      }
      const payload = {
        business_id: businessId,
        numero: undefined,
        cliente,
        tipos_proyecto: tipos,
        tipos_proyecto_extra: tiposExtra.split('\n').map(s => s.trim()).filter(Boolean),
        descripcion,
        direccion_proyecto: direccion,
        numero_cotizacion: numCot || null,
        fecha_inicio_estimada: fechaIni || null,
        fecha_entrega_estimada: fechaEnt || null,
        monto_total_cotizado: Number(montoCot || 0),
        anticipo_monto: Number(anticipo.monto || 0),
        anticipo_fecha: anticipo.fecha || null,
        anticipo_documento_url: anticipo.url || null,
        anticipo_documento_nombre: anticipo.nombre || null,
        bypass_pin: bypassPin || null,
        bypass_motivo: bypassMotivo || null,
        es_suministro: esSuministro,
      };
      const res = await api.post('/produccion/ot', payload);
      toast.success(`OT ${res.data.numero} creada`);
      onCreated(res.data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error creando OT');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] max-w-2xl w-full max-h-[92vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="modal-create-ot">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-black text-[var(--text)]">Nueva OT · paso {step}/3</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>

        {step === 1 && (
          <div className="space-y-3">
            <p className="text-xs font-bold uppercase text-[var(--text-muted)]">Datos generales</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase text-[var(--text-muted)]">Número OT (correlativo automático)</label>
                <input value={numero} readOnly disabled className="w-full mt-0.5 px-2 py-2 bg-[var(--surface-muted)] border border-[var(--border)] rounded text-sm opacity-70 cursor-not-allowed" data-testid="input-numero" />
                <p className="text-[10px] text-[var(--text-faint)] mt-0.5">Sigue el orden del CRM — no editable</p>
              </div>
              <div>
                <label className="text-[10px] uppercase text-[var(--text-muted)]">N° cotización vinculada</label>
                <input value={numCot} onChange={(e) => setNumCot(e.target.value)} placeholder="c26-6-9" className="w-full mt-0.5 px-2 py-2 bg-[var(--surface-muted)] border border-[var(--border)] rounded text-sm" />
              </div>
            </div>

            <p className="text-xs font-bold uppercase text-[var(--text-muted)] mt-3">Cliente</p>
            <input value={cliente.nombre} onChange={(e) => setCliente({ ...cliente, nombre: e.target.value })} placeholder="Nombre / razón social" className="w-full px-3 py-2 bg-[var(--surface-muted)] border border-[var(--border)] rounded text-sm" data-testid="input-cliente-nombre" />
            <div className="grid grid-cols-3 gap-2">
              <input value={cliente.telefono} onChange={(e) => setCliente({ ...cliente, telefono: e.target.value })} placeholder="Teléfono" className="px-3 py-2 bg-[var(--surface-muted)] border border-[var(--border)] rounded text-sm" />
              <input value={cliente.nit} onChange={(e) => setCliente({ ...cliente, nit: e.target.value })} placeholder="NIT" className="px-3 py-2 bg-[var(--surface-muted)] border border-[var(--border)] rounded text-sm" />
              <input value={cliente.email} onChange={(e) => setCliente({ ...cliente, email: e.target.value })} placeholder="Email" className="px-3 py-2 bg-[var(--surface-muted)] border border-[var(--border)] rounded text-sm" />
            </div>

            <input value={direccion} onChange={(e) => setDireccion(e.target.value)} placeholder="Dirección del proyecto" className="w-full px-3 py-2 bg-[var(--surface-muted)] border border-[var(--border)] rounded text-sm" data-testid="input-direccion" />
            <textarea value={descripcion} onChange={(e) => setDescripcion(e.target.value)} rows={3} placeholder="Descripción del proyecto" className="w-full px-3 py-2 bg-[var(--surface-muted)] border border-[var(--border)] rounded text-sm" data-testid="input-descripcion" />

            <label className="flex items-center gap-2 text-sm text-[var(--text)] p-2 bg-blue-500/10 border border-blue-500/30 rounded cursor-pointer" data-testid="checkbox-es-suministro">
              <input type="checkbox" checked={esSuministro} onChange={(e) => setEsSuministro(e.target.checked)} />
              <span><b>Solo suministro</b> (sin instalación) &mdash; la etapa &quot;Instalando&quot; se omite del timeline</span>
            </label>

            <div className="flex justify-end gap-2 pt-3 border-t border-[var(--border)]">
              <button onClick={onClose} className="px-3 py-2 text-sm font-bold text-[var(--text-muted)]">Cancelar</button>
              <button onClick={() => setStep(2)} disabled={!cliente.nombre} className="px-4 py-2 text-sm font-bold bg-orange-600 hover:bg-orange-700 text-white rounded-md disabled:opacity-40" data-testid="btn-step1-next">Siguiente →</button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <p className="text-xs font-bold uppercase text-[var(--text-muted)]">Tipo(s) de proyecto</p>
            <div className="grid grid-cols-2 gap-1 max-h-64 overflow-y-auto pr-1">
              {(catalog.tipos_proyecto || []).map(t => (
                <label key={t.value} className="flex items-center gap-1.5 text-xs cursor-pointer hover:bg-[var(--surface-muted)] rounded px-1.5 py-1">
                  <input type="checkbox" checked={tipos.includes(t.value)} onChange={() => toggleTipo(t.value)} data-testid={`tipo-${t.value}`} />
                  <span>{t.label}</span>
                </label>
              ))}
            </div>
            <div>
              <label className="text-[10px] uppercase text-[var(--text-muted)]">Otras líneas (una por renglón)</label>
              <textarea value={tiposExtra} onChange={(e) => setTiposExtra(e.target.value)} rows={2} placeholder="Ej. Cabina sauna" className="w-full mt-0.5 px-3 py-2 bg-[var(--surface-muted)] border border-[var(--border)] rounded text-xs" />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <div>
                <label className="text-[10px] uppercase text-[var(--text-muted)] block">Inicio estimado</label>
                <input type="date" min={todayISO} value={fechaIni} onChange={(e) => setFechaIni(e.target.value)} className="w-full mt-0.5 px-2 py-2 bg-[var(--surface-muted)] border border-[var(--border)] rounded text-xs" data-testid="ot-fecha-inicio" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-[var(--text-muted)] block">Entrega estimada</label>
                <input type="date" min={fechaIni || todayISO} value={fechaEnt} onChange={(e) => setFechaEnt(e.target.value)} className="w-full mt-0.5 px-2 py-2 bg-[var(--surface-muted)] border border-[var(--border)] rounded text-xs" data-testid="ot-fecha-entrega" />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <label className="text-[10px] uppercase text-[var(--text-muted)] block">Monto cotizado total (Q)</label>
                <input type="number" min="0" value={montoCot} onChange={(e) => setMontoCot(e.target.value)} placeholder="0.00" className="w-full mt-0.5 px-2 py-2 bg-[var(--surface-muted)] border border-[var(--border)] rounded text-xs" data-testid="input-monto-cotizado" />
              </div>
            </div>

            <div className="flex justify-between gap-2 pt-3 border-t border-[var(--border)]">
              <button onClick={() => setStep(1)} className="px-3 py-2 text-sm font-bold text-[var(--text-muted)]">← Atrás</button>
              <button onClick={() => setStep(3)} className="px-4 py-2 text-sm font-bold bg-orange-600 hover:bg-orange-700 text-white rounded-md" data-testid="btn-step2-next">Siguiente →</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-md p-3">
              <p className="text-xs text-amber-800 dark:text-amber-200 font-bold flex items-center gap-1.5">
                <Warning size={14} weight="fill" /> Anticipo obligatorio
              </p>
              <p className="text-[11px] text-amber-800 dark:text-amber-100/90 mt-1">
                Para crear una OT debes registrar el anticipo con factura/recibo. Si necesitas excepción usa PIN de Josué/Alejandro/Admin.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase text-[var(--text-muted)]">Monto anticipo (Q)</label>
                <input type="number" min="0" value={anticipo.monto} onChange={(e) => setAnticipo({ ...anticipo, monto: e.target.value })} className="w-full mt-0.5 px-2 py-2 bg-[var(--surface-muted)] border border-[var(--border)] rounded text-sm" data-testid="input-anticipo-monto" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-[var(--text-muted)]">Fecha anticipo</label>
                <input type="date" value={anticipo.fecha} onChange={(e) => setAnticipo({ ...anticipo, fecha: e.target.value })} className="w-full mt-0.5 px-2 py-2 bg-[var(--surface-muted)] border border-[var(--border)] rounded text-sm" />
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase text-[var(--text-muted)]">Documento de respaldo (factura/recibo)</label>
              <input type="file" accept="image/*,application/pdf" disabled={uploading}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const r = await uploadFile(file);
                  if (r) setAnticipo({ ...anticipo, url: r.url, nombre: r.nombre });
                }}
                className="w-full mt-0.5 text-xs"
                data-testid="input-anticipo-file"
              />
              {anticipo.url && <p className="text-[10px] text-emerald-400 mt-1"><CheckCircle size={10} className="inline" /> {anticipo.nombre}</p>}
              {uploading && <p className="text-[10px] text-blue-300">Subiendo…</p>}
            </div>

            <details className="bg-[var(--surface-muted)] rounded-md p-2">
              <summary className="text-xs font-bold text-amber-700 dark:text-amber-300 cursor-pointer">⚠ Crear sin anticipo (excepción con PIN)</summary>
              <div className="mt-2 space-y-2">
                <input type="password" value={bypassPin} onChange={(e) => setBypassPin(e.target.value)} placeholder="PIN de autorización" className="w-full px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs" data-testid="input-bypass-pin" />
                <textarea value={bypassMotivo} onChange={(e) => setBypassMotivo(e.target.value)} rows={2} placeholder="Motivo (mínimo 5 caracteres)" className="w-full px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs" />
              </div>
            </details>

            <div className="flex justify-between gap-2 pt-3 border-t border-[var(--border)]">
              <button onClick={() => setStep(2)} className="px-3 py-2 text-sm font-bold text-[var(--text-muted)]">← Atrás</button>
              <button onClick={submit} disabled={submitting} className="px-5 py-2 text-sm font-bold bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 text-white rounded-md disabled:opacity-50" data-testid="btn-create-ot">
                {submitting ? 'Creando…' : 'Crear OT'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


// ─────────────────────── Detail Modal with tabs ───────────────────────
const TABS = [
  { id: 'datos', label: 'Datos', icon: FileText },
  { id: 'pagos', label: 'Pagos', icon: CurrencyDollar },
  { id: 'venta_erp', label: 'Venta ERP', icon: Buildings },
  { id: 'materiales', label: 'Materiales', icon: Package },
  { id: 'mano_obra', label: 'Mano obra', icon: UsersIcon },
  { id: 'gastos', label: 'Gastos', icon: Receipt },
  { id: 'documentos', label: 'Documentos', icon: PaperclipHorizontal },
  { id: 'timeline', label: 'Timeline', icon: Clock },
  { id: 'rentabilidad', label: 'Rentabilidad', icon: ChartLine, restricted: true },
];

const OTDetailModal = ({ otId, role, onClose, onChanged }) => {
  const ctx = useOutletContext() || {};
  const hasTabPermission = ctx.hasTabPermission || (() => true);
  const [ot, setOt] = useState(null);
  const [tab, setTab] = useState('datos');
  const [showAudit, setShowAudit] = useState(false);
  const canRentability = ['admin', 'supervisor', 'gerente', 'financiero'].includes(role);
  const canAdminCosts = ['admin', 'supervisor', 'gerente', 'financiero'].includes(role);
  // Admin-like roles ven todas las tabs; otros se filtran por tab_permissions.produccion
  const isAdminLike = ['admin', 'supervisor', 'gerente'].includes(role);
  const visibleTabs = TABS.filter(t => {
    if (t.restricted && !canRentability) return false;
    if (isAdminLike) return true;
    if (t.id === 'venta_erp') return true; // Todos los que pueden abrir la OT pueden amarrar su factura ERP
    return hasTabPermission('produccion', t.id);
  });
  // Si la tab actual ya no es visible, mover a la primera disponible
  useEffect(() => {
    if (visibleTabs.length && !visibleTabs.find(t => t.id === tab)) {
      setTab(visibleTabs[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTabs.length]);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/produccion/ot/${otId}`);
      setOt(r.data);
    } catch (e) { toast.error('Error cargando OT'); }
  }, [otId]);

  useEffect(() => { load(); }, [load]);

  const changeEstado = async (newEstado) => {
    if (newEstado === 'cancelada') {
      setShowAnular(true);
      return;
    }
    try {
      await api.patch(`/produccion/ot/${otId}`, { estado: newEstado });
      await load();
      onChanged?.();
    } catch (e) { toast.error('Error'); }
  };

  const [showAnular, setShowAnular] = useState(false);
  const [showUnlockEdit, setShowUnlockEdit] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showEditPinPrompt, setShowEditPinPrompt] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  const downloadFile = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.error('Error generando archivo: ' + (e?.response?.status === 401 ? 'sesión expirada, vuelve a iniciar' : (e?.message || 'desconocido')));
    }
  };
  const exportPdf = () => downloadFile(`/produccion/ot/${otId}/export/pdf`, `OT_${ot?.numero || otId}_resumen.pdf`);
  const exportPdfForm = () => downloadFile(`/produccion/ot/${otId}/export/pdf-form`, `OT_${ot?.numero || otId}_formulario.pdf`);
  const exportXls = () => downloadFile(`/produccion/ot/${otId}/export/excel`, `OT_${ot?.numero || otId}.xlsx`);

  if (!ot) {
    return (
      <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center"><p className="text-white">Cargando…</p></div>
    );
  }

  const r = ot.rentabilidad || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-2 md:p-4" onClick={onClose}>
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] max-w-5xl w-full max-h-[95vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="modal-ot-detail">
        {/* Header */}
        <div className="px-4 py-3 border-b border-[var(--border)] flex items-center justify-between gap-2 shrink-0">
          <div>
            <h2 className={`text-lg font-black ${ot.estado === 'cancelada' ? 'text-red-600 line-through' : 'text-[var(--text)]'}`}>
              {ot.numero}
              {ot.estado === 'cancelada' && <span className="ml-2 text-[10px] px-2 py-0.5 bg-red-500/20 text-red-700 dark:text-red-300 rounded font-black no-underline inline-block align-middle">ANULADA</span>}
              {ot.es_suministro && <span className="ml-2 text-[10px] px-2 py-0.5 bg-blue-500/20 text-blue-700 dark:text-blue-300 rounded font-black no-underline inline-block align-middle">SOLO SUMINISTRO</span>}
            </h2>
            <p className="text-[11px] text-[var(--text-muted)]">{ot.cliente?.nombre} · {ot.business_name}</p>
            {ot.estado === 'cancelada' && ot.anulada_motivo && (
              <p className="text-[10px] text-red-600 dark:text-red-300 mt-0.5">Motivo: {ot.anulada_motivo} · Autorizado por {ot.anulada_autorizado_por || ot.anulada_por}</p>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap justify-end">
            <select value={ot.estado} onChange={(e) => changeEstado(e.target.value)} className="text-xs px-2 py-1.5 bg-[var(--surface-muted)] border border-[var(--border)] rounded font-bold" data-testid="select-estado" disabled={ot.estado === 'cancelada'}>
              {ESTADOS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
            </select>
            {ot.estado !== 'cancelada' && (() => {
              // Reglas de edición:
              // - PIN-holders (admin/supervisor/gerente/producción/financiero): pueden editar siempre
              // - Vendedores: solo si OT está 'abierta' O si tienen ventana de 15min activa
              const PIN_ROLES = ['admin', 'supervisor', 'gerente', 'financiero', 'produccion'];
              const isPinHolder = PIN_ROLES.includes(role);
              let hasActiveWindow = false;
              let minsLeft = 0;
              if (ot.edit_unlock_until) {
                const until = new Date(ot.edit_unlock_until);
                const now = new Date();
                if (until > now) {
                  hasActiveWindow = true;
                  minsLeft = Math.ceil((until - now) / 60000);
                }
              }
              const canEditFree = isPinHolder || ot.estado === 'abierta';
              const canEditRestricted = !canEditFree && hasActiveWindow;
              if (canEditFree) {
                return (
                  <button onClick={() => setShowEditPinPrompt(true)} className="px-2 py-1.5 text-[10px] font-bold border border-blue-500/40 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-500/10" title="Editar datos de la OT (requiere PIN)" data-testid="btn-edit-ot">
                    ✏️ Editar
                  </button>
                );
              }
              if (canEditRestricted) {
                return (
                  <button onClick={() => setShowEditPinPrompt(true)} className="px-2 py-1.5 text-[10px] font-bold border border-amber-500/60 bg-amber-500/10 text-amber-700 dark:text-amber-300 rounded hover:bg-amber-500/20" title={`Ventana de edición activa (${minsLeft} min restantes) — requiere PIN`} data-testid="btn-edit-ot-restricted">
                    ⏱️ Editar ({minsLeft}m)
                  </button>
                );
              }
              // Vendedor sin ventana activa — mostrar botón que abre solicitud de desbloqueo
              return (
                <button onClick={() => toast.info('Esta OT está bloqueada. Pide a un admin (Josué / Juanma / Alex) que la desbloquee desde ⋮ Más → Desbloquear edición.')} className="px-2 py-1.5 text-[10px] font-bold border border-[var(--border)] text-[var(--text-muted)] rounded hover:bg-[var(--surface-muted)] cursor-help" title="OT bloqueada. Pide desbloqueo a un supervisor con PIN." data-testid="btn-edit-locked">
                  🔒 Bloqueada
                </button>
              );
            })()}
            {ot.estado !== 'cancelada' && (
              <button onClick={() => setShowAnular(true)} className="px-2 py-1.5 text-[10px] font-bold border border-red-500/40 text-red-700 dark:text-red-300 rounded hover:bg-red-500/10" title="Anular OT (requiere PIN)" data-testid="btn-anular-ot">
                🚫 Anular
              </button>
            )}
            {/* Menú "Más" con las opciones de exportación y desbloqueo (compact para móvil) */}
            <div className="relative">
              <button
                onClick={() => setShowMoreMenu(v => !v)}
                className="px-2 py-1.5 text-[10px] font-bold border border-[var(--border)] text-[var(--text)] rounded hover:bg-[var(--surface-muted)]"
                title="Más opciones"
                data-testid="btn-more-menu"
              >
                ⋮ Más
              </button>
              {showMoreMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowMoreMenu(false)} />
                  <div className="absolute right-0 top-full mt-1 z-50 bg-[var(--surface)] border border-[var(--border)] rounded-md shadow-lg py-1 min-w-[220px]" data-testid="more-menu-dropdown">
                    <button onClick={() => { exportPdfForm(); setShowMoreMenu(false); }} className="w-full text-left px-3 py-2 text-xs hover:bg-[var(--surface-muted)] font-bold text-[var(--text)]">📋 Formulario OT (PDF completo)</button>
                    <button onClick={() => { exportPdf(); setShowMoreMenu(false); }} className="w-full text-left px-3 py-2 text-xs hover:bg-[var(--surface-muted)] text-[var(--text)]">📄 Resumen ejecutivo (PDF)</button>
                    <button onClick={() => { exportXls(); setShowMoreMenu(false); }} className="w-full text-left px-3 py-2 text-xs hover:bg-[var(--surface-muted)] text-[var(--text)]">📊 Exportar a Excel</button>
                    {isAdminLike && (
                      <>
                        <div className="border-t border-[var(--border)] my-1" />
                        <button onClick={() => { setShowAudit(true); setShowMoreMenu(false); }} className="w-full text-left px-3 py-2 text-xs hover:bg-[var(--surface-muted)] text-[var(--text)] font-bold" data-testid="menu-ot-audit">📜 Auditoría (historial de autorizaciones)</button>
                      </>
                    )}
                    {ot.estado !== 'cancelada' && ot.estado !== 'abierta' && (
                      <>
                        <div className="border-t border-[var(--border)] my-1" />
                        <button onClick={() => { setShowUnlockEdit(true); setShowMoreMenu(false); }} className="w-full text-left px-3 py-2 text-xs hover:bg-amber-500/10 text-amber-700 dark:text-amber-300 font-bold" data-testid="menu-unlock-edit">🔓 Desbloquear edición para vendedor (PIN, 15 min)</button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-[var(--surface-muted)] rounded" data-testid="btn-close-ot"><X size={18} /></button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[var(--border)] overflow-x-auto shrink-0">
          {visibleTabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`px-3 py-2 text-xs font-bold whitespace-nowrap border-b-2 ${tab === t.id ? 'border-orange-500 text-orange-300' : 'border-transparent text-[var(--text-muted)]'}`} data-testid={`tab-${t.id}`}>
              <t.icon size={12} weight="bold" className="inline mr-1" /> {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {tab === 'datos' && <TabDatos ot={ot} />}
          {tab === 'pagos' && <TabPagos ot={ot} onChanged={load} />}
          {tab === 'venta_erp' && <TabVentaERP ot={ot} onChanged={load} />}
          {tab === 'materiales' && <TabMateriales ot={ot} onChanged={load} />}
          {tab === 'mano_obra' && <TabManoObra ot={ot} onChanged={load} />}
          {tab === 'gastos' && <TabGastos ot={ot} onChanged={load} canAdminCosts={canAdminCosts} />}
          {tab === 'documentos' && <TabDocumentos ot={ot} onChanged={load} />}
          {tab === 'timeline' && <TabTimeline ot={ot} onChanged={load} />}
          {tab === 'rentabilidad' && canRentability && <TabRentabilidad r={r} ot={ot} />}
        </div>

        {showAnular && (
          <AnularOTModal
            ot={ot}
            onClose={() => setShowAnular(false)}
            onAnulada={async () => { setShowAnular(false); await load(); onChanged?.(); }}
          />
        )}
        {showUnlockEdit && (
          <PaymentPinPrompt
            title="Desbloquear edición de OT"
            description={`El vendedor ${ot.vendor_name || ''} podrá editar nombre/dirección/descripción por 15 min. Se le enviará aviso por WhatsApp.`}
            confirmLabel="Desbloquear (15 min)"
            onClose={() => setShowUnlockEdit(false)}
            onConfirm={async ({ pin, motivo, autorizado_por }) => {
              const res = await api.post(`/produccion/ot/${ot.id}/unlock-edit`, {
                pin, motivo, autorizado_por, minutos: 15,
              });
              if (res.data.whatsapp_sent) {
                toast.success(`✓ Edición desbloqueada por 15 min. WhatsApp enviado a ${ot.vendor_name}`);
              } else {
                toast.warning(`⚠ Edición desbloqueada pero WhatsApp no se envió: ${res.data.whatsapp_error || 'ver logs'}`);
              }
              setShowUnlockEdit(false);
              await load();
              onChanged?.();
            }}
          />
        )}
        {showEditPinPrompt && (
          <PaymentPinPrompt
            title={`🔐 Autorizar edición de OT ${ot.numero}`}
            description={`Debes ingresar el PIN de autorización para modificar los datos de esta OT. Toda edición queda registrada en el timeline con tu nombre y motivo.`}
            confirmLabel="Autorizar y editar"
            onClose={() => setShowEditPinPrompt(false)}
            onConfirm={async ({ pin, motivo, autorizado_por }) => {
              await api.post('/produccion/payment-pin/verify', {
                pin, motivo, accion: `edit_ot_${ot.numero}`, ot_id: ot.id,
                extra: { autorizado_por, kind: 'edit_ot_pin_gate' },
              });
              setShowEditPinPrompt(false);
              setShowEdit(true);
              toast.success('✓ PIN validado — abriendo editor');
            }}
          />
        )}
        {showEdit && (
          <EditOTModal
            ot={ot}
            role={role}
            onClose={() => setShowEdit(false)}
            onSaved={async () => { setShowEdit(false); await load(); onChanged?.(); }}
          />
        )}
        {showAudit && <OTAuditModal ot={ot} onClose={() => setShowAudit(false)} />}
      </div>
    </div>
  );
};

// ─────────────────────── AUDIT MODAL ───────────────────────
const OTAuditModal = ({ ot, onClose }) => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get(`/produccion/payment-audit?ot_id=${ot.id}`)
      .then(r => setItems(r.data.items || []))
      .catch(e => { toast.error(e?.response?.data?.detail || 'Error cargando auditoría'); setItems([]); });
  }, [ot.id]);

  const accionLabel = (a) => {
    const acc = a.accion || '';
    if (acc.startsWith('edit_ot')) return '✏️ Edición de OT';
    if (acc.includes('unlock')) return '🔓 Desbloqueo de edición';
    if (acc.includes('anular') || acc.includes('cancel')) return '🚫 Anulación';
    if (acc.includes('pago') || acc.includes('payment')) return '💰 Pago';
    return acc;
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="modal-ot-audit">
        <div className="px-4 py-3 border-b border-[var(--border)] flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-sm font-black text-[var(--text)]">📜 Auditoría — {ot.numero}</h3>
            <p className="text-[10px] text-[var(--text-muted)]">Historial de autorizaciones con PIN y ediciones de esta OT</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[var(--surface-muted)] rounded" data-testid="btn-close-audit"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {items === null ? (
            <p className="text-center text-xs text-[var(--text-muted)] py-8">Cargando…</p>
          ) : items.length === 0 ? (
            <p className="text-center text-xs text-[var(--text-muted)] py-8" data-testid="audit-empty">Sin registros de auditoría para esta OT.</p>
          ) : (
            <div className="space-y-2" data-testid="audit-list">
              {items.map((a) => (
                <div key={a.id} className="border border-[var(--border)] rounded-[10px] p-3 bg-[var(--surface-muted)]" data-testid={`audit-item-${a.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-xs font-black text-[var(--text)]">{accionLabel(a)}</span>
                    <span className="text-[10px] text-[var(--text-faint)] font-bold">
                      {a.created_at ? new Date(a.created_at).toLocaleString('es-GT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                  </div>
                  <p className="text-[11px] text-[var(--text-muted)] mt-1">
                    <b>Usuario:</b> {a.user_name || '—'}
                    {a.extra?.autorizado_por && <> · <b>Autorizó (PIN):</b> {a.extra.autorizado_por}</>}
                  </p>
                  {a.motivo && <p className="text-[11px] text-[var(--text-muted)] mt-0.5"><b>Motivo:</b> {a.motivo}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────── EDIT OT MODAL ───────────────────────
const EditOTModal = ({ ot, role, onClose, onSaved }) => {
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  // Vendedor con ventana activa → puede editar TODO (bajo responsabilidad de quien autorizó).
  // El único campo bloqueado es el N° de OT (crítico para trazabilidad).
  const PIN_ROLES = ['admin', 'supervisor', 'gerente', 'financiero', 'produccion'];
  const isPinHolder = PIN_ROLES.includes(role);
  const isVendorInWindow = !isPinHolder && (ot.estado !== 'abierta');
  const [minsLeft, setMinsLeft] = useState(() => {
    if (!ot.edit_unlock_until) return 0;
    const until = new Date(ot.edit_unlock_until);
    return Math.max(0, Math.ceil((until - new Date()) / 60000));
  });
  useEffect(() => {
    if (!isVendorInWindow || !ot.edit_unlock_until) return undefined;
    const t = setInterval(() => {
      const until = new Date(ot.edit_unlock_until);
      const left = Math.max(0, Math.ceil((until - new Date()) / 60000));
      setMinsLeft(left);
      if (left <= 0) {
        toast.warning('⏱ Ventana de edición expirada.');
      }
    }, 20000);
    return () => clearInterval(t);
  }, [isVendorInWindow, ot.edit_unlock_until]);

  const [f, setF] = useState({
    cliente_nombre: ot.cliente?.nombre || '',
    cliente_telefono: ot.cliente?.telefono || '',
    cliente_nit: ot.cliente?.nit || '',
    cliente_email: ot.cliente?.email || '',
    cliente_direccion: ot.cliente?.direccion || '',
    direccion_proyecto: ot.direccion_proyecto || '',
    descripcion: ot.descripcion || '',
    monto_total_cotizado: ot.monto_total_cotizado || 0,
    fecha_inicio_estimada: (ot.fecha_inicio_estimada || '').slice(0, 10),
    fecha_entrega_estimada: (ot.fecha_entrega_estimada || '').slice(0, 10),
    responsable_produccion_id: ot.responsable_produccion_id || '',
    es_suministro: !!ot.es_suministro,
  });

  useEffect(() => {
    // Solo cargar usuarios si vamos a mostrar el dropdown de "responsable producción"
    api.get('/users').then(r => setUsers(r.data || [])).catch(() => setUsers([]));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        descripcion: f.descripcion,
        direccion_proyecto: f.direccion_proyecto,
        fecha_inicio_estimada: f.fecha_inicio_estimada || null,
        fecha_entrega_estimada: f.fecha_entrega_estimada || null,
        monto_total_cotizado: Number(f.monto_total_cotizado || 0),
        responsable_produccion_id: f.responsable_produccion_id || null,
        es_suministro: f.es_suministro,
        cliente: {
          nombre: f.cliente_nombre,
          telefono: f.cliente_telefono || null,
          nit: f.cliente_nit || null,
          email: f.cliente_email || null,
          direccion: f.cliente_direccion || null,
        },
      };
      await api.patch(`/produccion/ot/${ot.id}`, payload);
      toast.success('OT actualizada');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const inp = 'w-full bg-[var(--bg)] border border-[var(--border)] rounded px-2 py-1.5 text-sm text-[var(--text)]';
  const lbl = 'block text-[10px] uppercase font-bold text-[var(--text-muted)] mb-1';

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-3" onClick={onClose} data-testid="modal-edit-ot">
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] max-w-2xl w-full max-h-[92vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-black text-[var(--text)]">✏️ Editar OT {ot.numero}</h3>
            <p className="text-[11px] text-[var(--text-muted)]">Actualiza los datos generales. Para pagos, materiales, mano de obra o gastos usa las pestañas del detalle.</p>
          </div>
          <button onClick={onClose}><X size={20} /></button>
        </div>

        {isVendorInWindow && (
          <div className="mb-3 p-3 bg-amber-500/10 border border-amber-500/40 rounded" data-testid="edit-window-banner">
            <div className="flex items-start gap-2">
              <span className="text-lg">⏱️</span>
              <div className="flex-1">
                <p className="text-xs font-black text-amber-700 dark:text-amber-300">Ventana de edición activa — {minsLeft} min restantes</p>
                <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-0.5">
                  Puedes editar TODOS los campos (incluyendo monto y fechas).
                  {ot.edit_authorized_by && <> Autorizado por <b>{ot.edit_authorized_by}</b>.</>}
                  {ot.edit_authorized_motivo && <> Motivo: <i>{ot.edit_authorized_motivo}</i>.</>}
                </p>
                <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">Cada cambio queda registrado en el timeline con tu nombre y la referencia del autorizador.</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2 pb-2 border-b border-[var(--border)]">
            <p className="text-[10px] uppercase font-black text-[var(--text-muted)] mb-1">👤 Cliente</p>
          </div>
          <div>
            <label className={lbl}>Nombre del cliente *</label>
            <input value={f.cliente_nombre} onChange={(e) => setF({...f, cliente_nombre: e.target.value})} className={inp} data-testid="edit-cliente-nombre" />
          </div>
          <div>
            <label className={lbl}>Teléfono</label>
            <input value={f.cliente_telefono} onChange={(e) => setF({...f, cliente_telefono: e.target.value})} className={inp} />
          </div>
          <div>
            <label className={lbl}>NIT</label>
            <input value={f.cliente_nit} onChange={(e) => setF({...f, cliente_nit: e.target.value})} className={inp} />
          </div>
          <div>
            <label className={lbl}>Email</label>
            <input type="email" value={f.cliente_email} onChange={(e) => setF({...f, cliente_email: e.target.value})} className={inp} />
          </div>
          <div className="sm:col-span-2">
            <label className={lbl}>Dirección del cliente</label>
            <input value={f.cliente_direccion} onChange={(e) => setF({...f, cliente_direccion: e.target.value})} className={inp} />
          </div>

          <div className="sm:col-span-2 pt-3 pb-2 border-b border-[var(--border)]">
            <p className="text-[10px] uppercase font-black text-[var(--text-muted)] mb-1">🏗️ Proyecto</p>
          </div>
          <div className="sm:col-span-2">
            <label className={lbl}>Dirección del proyecto</label>
            <input value={f.direccion_proyecto} onChange={(e) => setF({...f, direccion_proyecto: e.target.value})} className={inp} data-testid="edit-direccion-proyecto" />
          </div>
          <div className="sm:col-span-2">
            <label className={lbl}>Descripción del trabajo</label>
            <textarea rows={3} value={f.descripcion} onChange={(e) => setF({...f, descripcion: e.target.value})} className={inp} data-testid="edit-descripcion" />
          </div>
          <div>
            <label className={lbl}>Inicio estimado</label>
            <input type="date" value={f.fecha_inicio_estimada} onChange={(e) => setF({...f, fecha_inicio_estimada: e.target.value})} className={inp} />
          </div>
          <div>
            <label className={lbl}>Entrega estimada</label>
            <input type="date" value={f.fecha_entrega_estimada} onChange={(e) => setF({...f, fecha_entrega_estimada: e.target.value})} className={inp} />
          </div>
          <div>
            <label className={lbl}>Monto total cotizado (Q) {isVendorInWindow && <span className="text-amber-500">⚠</span>}</label>
            <input type="number" step="0.01" value={f.monto_total_cotizado} onChange={(e) => setF({...f, monto_total_cotizado: e.target.value})} className={inp} data-testid="edit-monto" />
          </div>
          <div>
            <label className={lbl}>Responsable de producción</label>
            <select value={f.responsable_produccion_id} onChange={(e) => setF({...f, responsable_produccion_id: e.target.value})} className={inp} data-testid="edit-resp-produccion">
              <option value="">— sin asignar —</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer p-2 bg-blue-500/5 border border-blue-500/20 rounded">
              <input type="checkbox" checked={f.es_suministro} onChange={(e) => setF({...f, es_suministro: e.target.checked})} data-testid="edit-es-suministro" />
              <span className="text-[var(--text)]"><b>Solo suministro</b> (sin instalación — no pasa por fase &quot;Instalando&quot;)</span>
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-[var(--border)]">
          <button onClick={onClose} disabled={saving} className="px-3 py-1.5 text-xs font-bold text-[var(--text-muted)] hover:text-[var(--text)]">Cancelar</button>
          <button onClick={save} disabled={saving} className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded text-xs font-bold" data-testid="edit-ot-save">
            {saving ? 'Guardando…' : '💾 Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────── ANULAR OT MODAL ───────────────────────────
const AnularOTModal = ({ ot, onClose, onAnulada }) => {
  const [pin, setPin] = useState('');
  const [motivo, setMotivo] = useState('');
  const [autorizador, setAutorizador] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (!pin || !motivo || motivo.trim().length < 5) {
      toast.error('Completa PIN + motivo (mínimo 5 caracteres)');
      return;
    }
    if (!autorizador.trim()) {
      toast.error('Indica quién autoriza (Alejandro, Josué, Juanma o Admin)');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/produccion/ot/${ot.id}/anular`, {
        pin, motivo: motivo.trim(), autorizado_por: autorizador.trim(),
      });
      toast.success('OT anulada y registrada en bitácora');
      onAnulada();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al anular');
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[var(--surface)] border border-red-500/40 rounded-[14px] max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="modal-anular-ot">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-full bg-red-500/20 flex items-center justify-center text-xl">🚫</div>
          <div>
            <h3 className="text-base font-black text-[var(--text)]">Anular OT {ot.numero}</h3>
            <p className="text-[11px] text-[var(--text-muted)]">Requiere PIN + motivo + autorizador. Queda registrado en bitácora.</p>
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase font-bold text-[var(--text-muted)]">Autoriza (Alejandro / Josué / Juanma / Admin)</label>
            <input value={autorizador} onChange={(e) => setAutorizador(e.target.value)} placeholder="Nombre del autorizador" className="w-full mt-1 px-2 py-1.5 bg-[var(--surface-muted)] border border-[var(--border)] rounded text-sm" data-testid="anular-autorizador" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-[var(--text-muted)]">PIN de autorización</label>
            <input type="password" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="PIN" autoComplete="new-password" className="w-full mt-1 px-2 py-1.5 bg-[var(--surface-muted)] border border-[var(--border)] rounded text-sm" data-testid="anular-pin" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-[var(--text-muted)]">Motivo (≥5 caracteres)</label>
            <textarea rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Explica por qué se anula" className="w-full mt-1 px-2 py-1.5 bg-[var(--surface-muted)] border border-[var(--border)] rounded text-sm" data-testid="anular-motivo" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 text-xs font-bold rounded border border-[var(--border)] text-[var(--text-muted)]">Cancelar</button>
          <button onClick={submit} disabled={submitting} className="px-3 py-1.5 text-xs font-bold rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-50" data-testid="anular-submit">
            {submitting ? 'Anulando…' : 'Anular OT'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────── PAYMENT PIN MODAL (genérico para cambios sensibles) ───────────────────────
export const PaymentPinPrompt = ({ title, description, onClose, onConfirm, confirmLabel = 'Confirmar' }) => {
  const [pin, setPin] = useState('');
  const [motivo, setMotivo] = useState('');
  const [autorizador, setAutorizador] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (!pin || !motivo || motivo.trim().length < 5) { toast.error('Completa PIN + motivo (≥5 caracteres)'); return; }
    if (!autorizador.trim()) { toast.error('Indica quién autoriza'); return; }
    setSubmitting(true);
    try {
      await onConfirm({ pin, motivo: motivo.trim(), autorizado_por: autorizador.trim() });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error de autorización');
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[var(--surface)] border border-amber-500/40 rounded-[14px] max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="modal-payment-pin">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-full bg-amber-500/20 flex items-center justify-center text-xl">🔐</div>
          <div>
            <h3 className="text-base font-black text-[var(--text)]">{title}</h3>
            <p className="text-[11px] text-[var(--text-muted)]">{description}</p>
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase font-bold text-[var(--text-muted)]">Autoriza (Alejandro / Josué / Juanma / Admin)</label>
            <input value={autorizador} onChange={(e) => setAutorizador(e.target.value)} placeholder="Nombre del autorizador" className="w-full mt-1 px-2 py-1.5 bg-[var(--surface-muted)] border border-[var(--border)] rounded text-sm" data-testid="pin-autorizador" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-[var(--text-muted)]">PIN</label>
            <input type="password" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="PIN" autoComplete="new-password" className="w-full mt-1 px-2 py-1.5 bg-[var(--surface-muted)] border border-[var(--border)] rounded text-sm" data-testid="pin-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-[var(--text-muted)]">Motivo (≥5 caracteres)</label>
            <textarea rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} className="w-full mt-1 px-2 py-1.5 bg-[var(--surface-muted)] border border-[var(--border)] rounded text-sm" data-testid="pin-motivo" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 text-xs font-bold rounded border border-[var(--border)] text-[var(--text-muted)]">Cancelar</button>
          <button onClick={submit} disabled={submitting} className="px-3 py-1.5 text-xs font-bold rounded bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50" data-testid="pin-submit">
            {submitting ? 'Verificando…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, children }) => (
  <div className="grid grid-cols-3 gap-2 py-1.5 border-b border-[var(--border)]/40">
    <span className="text-xs text-[var(--text-muted)]">{label}</span>
    <span className="col-span-2 text-xs text-[var(--text)] font-bold">{children || '—'}</span>
  </div>
);

const TabDatos = ({ ot }) => {
  const tipos = [...(ot.tipos_proyecto || []), ...(ot.tipos_proyecto_extra || [])];
  return (
    <div className="space-y-1">
      <Row label="N° OT">{ot.numero}</Row>
      <Row label="Cliente">{ot.cliente?.nombre}</Row>
      <Row label="Tel · NIT">{[ot.cliente?.telefono, ot.cliente?.nit].filter(Boolean).join(' · ') || '—'}</Row>
      <Row label="Tipos">{tipos.join(', ') || '—'}</Row>
      <Row label="Dirección">{ot.direccion_proyecto}</Row>
      <Row label="Descripción">{ot.descripcion}</Row>
      <Row label="N° Cotización">{ot.numero_cotizacion}</Row>
      <Row label="Vendedor">{ot.vendor_name}</Row>
      <Row label="Resp. producción">{ot.responsable_produccion_nombre}</Row>
      <Row label="Inicio estimado">{fmtDate(ot.fecha_inicio_estimada)}</Row>
      <Row label="Entrega estimada">{fmtDate(ot.fecha_entrega_estimada)}</Row>
      <Row label="Entregada el">{fmtDate(ot.fecha_entregada)}</Row>
      <Row label="Monto cotizado">{fmtQ(ot.monto_total_cotizado)}</Row>
    </div>
  );
};

// ─────────── Reusable Sub-item creator hook ───────────
const useUpload = () => {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return null;
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/produccion/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      return r.data;
    } catch (e) {
      toast.error('Error subiendo archivo');
      return null;
    } finally { setUploading(false); }
  };
  return { upload, uploading };
};

const TabVentaERP = ({ ot, onChanged }) => {
  const vinculadas = ot.ventas_erp || [];
  const [q, setQ] = useState(ot.cliente?.nombre || '');
  const [empresa, setEmpresa] = useState('soluciones');
  const [resultados, setResultados] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const totalVinculado = vinculadas.reduce((s, v) => s + Number(v.total || 0), 0);

  const buscar = async () => {
    setBuscando(true);
    try {
      const params = new URLSearchParams({ empresa });
      if (q.trim()) params.set('q', q.trim());
      const r = await api.get(`/produccion/harperp-ventas?${params}`);
      setResultados(r.data.ventas || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error consultando HarpERP'); }
    setBuscando(false);
  };

  const vincular = async (v) => {
    try {
      await api.post(`/produccion/ot/${ot.id}/vincular-venta-erp`, {
        venta_id: v.id, empresa, sucursal_id: v.sucursal_id,
        snapshot: { documento: v.documento, tipo_documento: v.tipo_documento, fecha: v.fecha, total: v.total, cliente: v.cliente, vendedor: v.vendedor, sucursal: v.sucursal, certificada: v.certificada, uuid: v.uuid },
      });
      toast.success('✓ Venta vinculada a la OT');
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error vinculando'); }
  };

  const desvincular = async (ref) => {
    if (!window.confirm(`¿Desvincular ${ref.documento || ref.venta_id} de esta OT?`)) return;
    try {
      await api.delete(`/produccion/ot/${ot.id}/venta-erp/${ref.id}`);
      toast.success('Venta desvinculada');
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  return (
    <div data-testid="tab-venta-erp-content">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <h3 className="text-sm font-black flex items-center gap-1.5"><Buildings size={15} weight="fill" className="text-emerald-500" /> Ventas HarpERP amarradas a esta OT</h3>
          <p className="text-[10px] text-[var(--text-muted)]">Facturas SAT o notas de envío del ERP que respaldan este proyecto.</p>
        </div>
        {vinculadas.length > 0 && (
          <div className="text-right">
            <p className="text-[10px] uppercase font-bold text-[var(--text-muted)]">Total vinculado ERP</p>
            <p className="text-base font-black text-emerald-500" data-testid="total-vinculado-erp">{fmtQ(totalVinculado)}</p>
            {Number(ot.monto_total_cotizado) > 0 && (
              <p className="text-[10px] text-[var(--text-muted)]">vs cotizado {fmtQ(ot.monto_total_cotizado)} ({Math.round(totalVinculado / Number(ot.monto_total_cotizado) * 100)}%)</p>
            )}
          </div>
        )}
      </div>

      {vinculadas.length === 0 ? (
        <p className="text-xs text-[var(--text-muted)] italic mb-4">Sin ventas del ERP vinculadas todavía.</p>
      ) : (
        <div className="space-y-1.5 mb-4">
          {vinculadas.map((v) => (
            <div key={v.id} className="flex items-center gap-2 bg-[var(--surface-muted)] border border-[var(--border)] rounded-[10px] px-3 py-2" data-testid={`venta-erp-${v.id}`}>
              <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded shrink-0 ${v.tipo_doc === 'envio' ? 'bg-amber-500/15 text-amber-600' : 'bg-emerald-500/15 text-emerald-600'}`}>
                {v.tipo_doc === 'envio' ? '🚚 Envío' : '📄 Factura'}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold truncate">{v.documento || `Venta #${v.venta_id}`} · {fmtQ(v.total)}</p>
                <p className="text-[10px] text-[var(--text-muted)] truncate">
                  {v.fecha} · {v.cliente} · {v.empresa === 'soluciones' ? 'SA' : 'CyH'}{v.certificada ? ' · FEL ✅' : ''} · vinculó {v.linked_by}
                </p>
              </div>
              <button onClick={() => desvincular(v)} className="p-1 text-red-400 hover:bg-red-500/10 rounded shrink-0" data-testid={`btn-desvincular-${v.id}`}><Trash size={14} /></button>
            </div>
          ))}
        </div>
      )}

      <div className="border border-[var(--border)] rounded-[12px] p-3 bg-[var(--surface)]">
        <p className="text-[11px] font-black uppercase text-[var(--text-muted)] mb-2">Buscar venta en HarpERP (últimos 60 días)</p>
        <div className="flex gap-2 flex-wrap items-center mb-2">
          <select value={empresa} onChange={(e) => setEmpresa(e.target.value)}
            className="bg-[var(--surface-muted)] border border-[var(--border)] rounded-md px-2 py-2 text-xs" data-testid="select-empresa-erp">
            <option value="soluciones">Soluciones (SA)</option>
            <option value="complementos">Complementos (CyH)</option>
          </select>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cliente…"
            className="flex-1 min-w-[140px] bg-[var(--surface-muted)] border border-[var(--border)] rounded-md px-3 py-2 text-xs" data-testid="input-buscar-venta-erp" />
          <button onClick={buscar} disabled={buscando}
            className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-2 rounded-md disabled:opacity-50" data-testid="btn-buscar-venta-erp">
            <MagnifyingGlass size={13} /> {buscando ? 'Buscando…' : 'Buscar'}
          </button>
        </div>
        {resultados !== null && (
          resultados.length === 0 ? <p className="text-[11px] text-[var(--text-muted)] italic">Sin resultados en el período.</p> : (
            <div className="space-y-1 max-h-56 overflow-y-auto">
              {resultados.map((v) => {
                const ya = vinculadas.some((x) => x.venta_id === v.id && x.empresa === empresa);
                return (
                  <div key={`${empresa}-${v.id}`} className="flex items-center gap-2 border border-[var(--border)] rounded-[8px] px-2.5 py-1.5">
                    <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded shrink-0 ${v.tipo_doc === 'envio' ? 'bg-amber-500/15 text-amber-600' : 'bg-emerald-500/15 text-emerald-600'}`}>
                      {v.tipo_doc === 'envio' ? 'Envío' : 'Factura'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-bold truncate">{v.documento} · {fmtQ(v.total)}</p>
                      <p className="text-[10px] text-[var(--text-muted)] truncate">{v.fecha} · {v.cliente} · {v.vendedor}</p>
                    </div>
                    <button onClick={() => vincular(v)} disabled={ya}
                      className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40 shrink-0"
                      data-testid={`btn-vincular-${v.id}`}>
                      <LinkSimple size={11} /> {ya ? 'Vinculada' : 'Vincular'}
                    </button>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>
    </div>
  );
};

const TabPagos = ({ ot, onChanged }) => {
  const [tipo, setTipo] = useState('parcial');
  const [monto, setMonto] = useState('');
  const [fecha, setFecha] = useState('');
  const [notas, setNotas] = useState('');
  const [doc, setDoc] = useState(null);
  const [bypassPin, setBypassPin] = useState('');
  const { upload, uploading } = useUpload();

  const add = async () => {
    if (!monto) return toast.error('Monto requerido');
    try {
      await api.post(`/produccion/ot/${ot.id}/pago`, {
        tipo, monto: Number(monto), fecha: fecha || null,
        documento_url: doc?.url || null, documento_nombre: doc?.nombre || null,
        notas: notas || null, bypass_pin: bypassPin || null,
      });
      toast.success('Pago registrado');
      setMonto(''); setNotas(''); setDoc(null); setBypassPin('');
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const [pinPayment, setPinPayment] = useState(null); // pago a eliminar pendiente de PIN
  const remove = (id) => {
    setPinPayment(id);
  };
  const confirmRemove = async ({ pin, motivo, autorizado_por }) => {
    try {
      await api.delete(`/produccion/ot/${ot.id}/pagos/${pinPayment}`, {
        params: { pin, motivo, autorizado_por },
      });
      toast.success('Pago eliminado y registrado en bitácora');
      setPinPayment(null);
      onChanged();
    } catch (e) {
      throw e;
    }
  };

  return (
    <div className="space-y-3">
      <div className="bg-[var(--surface-muted)] rounded-md p-3 space-y-2">
        <p className="text-xs font-bold uppercase text-[var(--text-muted)]">Registrar nuevo pago</p>
        <div className="grid grid-cols-3 gap-2">
          <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs">
            <option value="anticipo">Anticipo</option>
            <option value="parcial">Parcial</option>
            <option value="final">Final</option>
          </select>
          <input type="number" value={monto} onChange={(e) => setMonto(e.target.value)} placeholder="Monto" className="px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs" />
          <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs" />
        </div>
        <input type="file" disabled={uploading} onChange={async (e) => { const r = await upload(e.target.files?.[0]); if (r) setDoc(r); }} className="text-xs" />
        {doc && <p className="text-[10px] text-emerald-400">📎 {doc.nombre}</p>}
        <input value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Notas (opcional)" className="w-full px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs" />
        <details>
          <summary className="text-[11px] text-amber-700 dark:text-amber-300 cursor-pointer">⚠ Registrar sin documento (PIN)</summary>
          <input type="password" value={bypassPin} onChange={(e) => setBypassPin(e.target.value)} placeholder="PIN" className="mt-2 w-full px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs" />
        </details>
        <button onClick={add} className="w-full px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-xs font-bold">+ Agregar pago</button>
      </div>

      <table className="w-full text-xs">
        <thead><tr className="text-[10px] uppercase text-[var(--text-muted)] border-b border-[var(--border)]">
          <th className="text-left py-1">Tipo</th><th className="text-right py-1">Monto</th><th className="text-left py-1">Fecha</th><th className="text-center py-1">Doc</th><th></th>
        </tr></thead>
        <tbody>
          {(ot.pagos || []).map(p => (
            <tr key={p.id} className="border-b border-[var(--border)]/40">
              <td className="py-1 capitalize">{p.tipo}</td>
              <td className="py-1 text-right font-bold text-emerald-400">{fmtQ(p.monto)}</td>
              <td className="py-1 text-[var(--text-muted)]">{fmtDate(p.fecha)}</td>
              <td className="py-1 text-center">{p.documento_url ? <a href={p.documento_url} target="_blank" rel="noreferrer">📎</a> : '—'}</td>
              <td className="py-1 text-right"><button onClick={() => remove(p.id)} className="text-red-400"><Trash size={11} /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
      {pinPayment && (
        <PaymentPinPrompt
          title="Eliminar pago"
          description="Eliminar un pago requiere PIN + motivo + autorizador. Queda en bitácora."
          onClose={() => setPinPayment(null)}
          onConfirm={confirmRemove}
          confirmLabel="Eliminar pago"
        />
      )}
    </div>
  );
};

const DocsCotizados = ({ ot, onChanged }) => {
  const { upload, uploading } = useUpload();
  const [pend, setPend] = useState(null); // archivo subido pendiente de datos
  const [dDesc, setDDesc] = useState('');
  const [dValor, setDValor] = useState('');
  const docs = ot.docs_cotizados || [];
  const totalCotizado = docs.reduce((s, d) => s + Number(d.valor || 0), 0);
  const totalGastos = (ot.gastos || []).reduce((s, g) => s + Number(g.monto || 0), 0)
    + (ot.materiales || []).filter((m) => m.fase === 'comprado').reduce((s, m) => s + Number(m.subtotal || 0), 0);
  const dif = totalCotizado - totalGastos;

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const r = await upload(file);
    if (r) setPend(r);
  };

  const guardar = async () => {
    if (!dDesc.trim()) return toast.error('Indica qué tipo de documento es (descripción)');
    if (!Number(dValor) || Number(dValor) <= 0) return toast.error('Indica el valor cotizado en Q');
    try {
      await api.post(`/produccion/ot/${ot.id}/doc-cotizado`, {
        url: pend.url, nombre: pend.nombre, content_type: pend.content_type,
        descripcion: dDesc.trim(), valor: Number(dValor),
      });
      toast.success('✓ Documento de cotización adjuntado');
      setPend(null); setDDesc(''); setDValor('');
      onChanged();
    } catch (e2) { toast.error(e2?.response?.data?.detail || 'Error guardando'); }
  };

  const eliminar = async (d) => {
    if (!window.confirm(`¿Eliminar "${d.descripcion}"?`)) return;
    try { await api.delete(`/produccion/ot/${ot.id}/doc-cotizado/${d.id}`); onChanged(); }
    catch (e2) { toast.error(e2?.response?.data?.detail || 'Error'); }
  };

  const icono = (d) => {
    const n = `${d.nombre || ''}${d.content_type || ''}`.toLowerCase();
    if (n.includes('pdf')) return '📄';
    if (n.includes('xls') || n.includes('sheet') || n.includes('csv')) return '📊';
    if (n.includes('jp') || n.includes('png') || n.includes('image')) return '🖼️';
    return '📎';
  };

  return (
    <div className="border-2 border-blue-500/30 bg-blue-500/5 rounded-[12px] p-3" data-testid="docs-cotizados-section">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div>
          <p className="text-xs font-black">📋 Documentos de lo cotizado <span className="text-[10px] font-bold text-[var(--text-muted)]">(guía para comparar vs lo comprado)</span></p>
        </div>
        {docs.length > 0 && (
          <div className="text-right text-[10px]" data-testid="comparativo-cotizado-comprado">
            <p><b>Cotizado:</b> {fmtQ(totalCotizado)} · <b>Comprado (gastos + materiales):</b> {fmtQ(totalGastos)}</p>
            <p className={`font-black ${dif >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
              {dif >= 0 ? `✓ Dentro de lo cotizado (margen ${fmtQ(dif)})` : `⚠️ Excedido en ${fmtQ(-dif)}`}
            </p>
          </div>
        )}
      </div>

      {docs.length > 0 && (
        <div className="space-y-1.5 mb-2">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-2 bg-[var(--surface)] border border-[var(--border)] rounded-[8px] px-2.5 py-1.5" data-testid={`doc-cotizado-${d.id}`}>
              <span className="text-base shrink-0">{icono(d)}</span>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-bold truncate">{d.descripcion} · <span className="text-emerald-500">{fmtQ(d.valor)}</span></p>
                <p className="text-[9px] text-[var(--text-muted)] truncate">{d.nombre} · subió {d.uploaded_by} · {(d.uploaded_at || '').slice(0, 10)}</p>
              </div>
              <a href={d.url} target="_blank" rel="noreferrer" download={d.nombre}
                className="text-[10px] font-bold text-blue-500 hover:underline shrink-0 px-1.5 py-1 border border-blue-500/40 rounded"
                data-testid={`descargar-doc-${d.id}`}>⬇ Descargar</a>
              <button onClick={() => eliminar(d)} className="text-red-400 p-1 shrink-0" data-testid={`eliminar-doc-${d.id}`}><Trash size={12} /></button>
            </div>
          ))}
        </div>
      )}

      {!pend ? (
        <label className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-2 rounded-md cursor-pointer ${uploading ? 'bg-[var(--surface-muted)] text-[var(--text-muted)]' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}>
          <PaperclipHorizontal size={13} /> {uploading ? 'Subiendo…' : 'Adjuntar documento (PDF, Excel, foto)'}
          <input type="file" accept=".pdf,.xls,.xlsx,.csv,image/*" className="hidden" disabled={uploading} onChange={onFile} data-testid="input-doc-cotizado" />
        </label>
      ) : (
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[10px] p-2.5 space-y-2" data-testid="form-doc-cotizado">
          <p className="text-[10px] font-bold text-emerald-500">📎 {pend.nombre} subido — completa los datos:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input value={dDesc} onChange={(e) => setDDesc(e.target.value)} placeholder="¿Qué documento es? (ej: Cotización proveedor vidrio)"
              className="px-2 py-1.5 bg-[var(--surface-muted)] border border-[var(--border)] rounded text-xs" data-testid="input-doc-descripcion" />
            <input type="number" value={dValor} onChange={(e) => setDValor(e.target.value)} placeholder="Valor cotizado Q"
              className="px-2 py-1.5 bg-[var(--surface-muted)] border border-[var(--border)] rounded text-xs" data-testid="input-doc-valor" />
          </div>
          <div className="flex gap-2">
            <button onClick={guardar} className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[11px] font-bold" data-testid="btn-guardar-doc">Guardar</button>
            <button onClick={() => { setPend(null); setDDesc(''); setDValor(''); }} className="px-3 py-1.5 border border-[var(--border)] rounded text-[11px]">Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
};

const TabMateriales = ({ ot, onChanged }) => {
  const [fase, setFase] = useState('cotizado');
  const [desc, setDesc] = useState('');
  const [cant, setCant] = useState('1');
  const [pu, setPu] = useState('');
  const [prov, setProv] = useState('');
  const [provNit, setProvNit] = useState('');
  const [factura, setFactura] = useState('');
  const [doc, setDoc] = useState(null);
  const { upload, uploading } = useUpload();

  const add = async () => {
    if (!desc) return toast.error('Descripción requerida');
    try {
      await api.post(`/produccion/ot/${ot.id}/material`, {
        fase, descripcion: desc, cantidad: Number(cant || 1), precio_unitario: Number(pu || 0),
        proveedor: prov || null, proveedor_nit: provNit || null,
        factura_numero: factura || null,
        documento_url: doc?.url || null, documento_nombre: doc?.nombre || null,
      });
      toast.success('Material agregado');
      setDesc(''); setCant('1'); setPu(''); setProv(''); setProvNit(''); setFactura(''); setDoc(null);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const remove = async (id) => { if (window.confirm('¿Eliminar?')) { await api.delete(`/produccion/ot/${ot.id}/materiales/${id}`); onChanged(); } };

  const cotizados = (ot.materiales || []).filter(m => m.fase === 'cotizado');
  const comprados = (ot.materiales || []).filter(m => m.fase === 'comprado');

  return (
    <div className="space-y-3">
      <DocsCotizados ot={ot} onChanged={onChanged} />
      <div className="bg-[var(--surface-muted)] rounded-md p-3 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <select value={fase} onChange={(e) => setFase(e.target.value)} className="px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs">
            <option value="cotizado">Cotizado</option>
            <option value="comprado">Comprado (requiere factura)</option>
          </select>
          <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Descripción" className="px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs" />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <input type="number" value={cant} onChange={(e) => setCant(e.target.value)} placeholder="Cant." className="px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs" />
          <input type="number" value={pu} onChange={(e) => setPu(e.target.value)} placeholder="P. Unitario" className="px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs" />
          <input value={factura} onChange={(e) => setFactura(e.target.value)} placeholder="N° Factura" className="px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input value={prov} onChange={(e) => setProv(e.target.value)} placeholder="Proveedor" className="px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs" />
          <input value={provNit} onChange={(e) => setProvNit(e.target.value)} placeholder="NIT proveedor" className="px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs" />
        </div>
        <input type="file" disabled={uploading} onChange={async (e) => { const r = await upload(e.target.files?.[0]); if (r) setDoc(r); }} className="text-xs" />
        {doc && <p className="text-[10px] text-emerald-400">📎 {doc.nombre}</p>}
        <button onClick={add} className="w-full px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-xs font-bold">+ Agregar material</button>
      </div>

      {['Cotizado', 'Comprado'].map((label, idx) => {
        const list = idx === 0 ? cotizados : comprados;
        const total = list.reduce((s, m) => s + (m.subtotal || 0), 0);
        return (
          <div key={label}>
            <p className="text-xs font-bold uppercase text-[var(--text-muted)] mb-1">{label} — total {fmtQ(total)}</p>
            <table className="w-full text-xs">
              <thead><tr className="text-[10px] uppercase text-[var(--text-muted)] border-b border-[var(--border)]">
                <th className="text-left py-1">Descripción</th><th className="text-right py-1">Cant</th><th className="text-right py-1">P.U.</th><th className="text-right py-1">Subtotal</th><th className="text-left py-1">Proveedor</th><th></th>
              </tr></thead>
              <tbody>{list.map(m => (
                <tr key={m.id} className="border-b border-[var(--border)]/40">
                  <td className="py-1">{m.descripcion}</td>
                  <td className="py-1 text-right">{m.cantidad}</td>
                  <td className="py-1 text-right">{fmtQ(m.precio_unitario)}</td>
                  <td className="py-1 text-right font-bold">{fmtQ(m.subtotal)}</td>
                  <td className="py-1 text-[var(--text-muted)]">{m.proveedor || '—'}</td>
                  <td className="py-1 text-right">{m.documento_url && <a href={m.documento_url} target="_blank" rel="noreferrer">📎</a>}<button onClick={() => remove(m.id)} className="text-red-400 ml-1"><Trash size={11} /></button></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
};

const TabManoObra = ({ ot, onChanged }) => {
  const [form, setForm] = useState({ instalador_nombre: '', rol: 'instalador', horas_normales: '', horas_extra: '', monto_total: '', fecha: '', notas: '' });
  const [doc, setDoc] = useState(null);
  const { upload, uploading } = useUpload();

  const add = async () => {
    if (!form.instalador_nombre) return toast.error('Nombre del instalador requerido');
    try {
      await api.post(`/produccion/ot/${ot.id}/mano-obra`, {
        ...form,
        horas_normales: Number(form.horas_normales || 0),
        horas_extra: Number(form.horas_extra || 0),
        monto_total: Number(form.monto_total || 0),
        fecha: form.fecha || null,
        documento_url: doc?.url || null, documento_nombre: doc?.nombre || null,
      });
      toast.success('Mano de obra agregada');
      setForm({ instalador_nombre: '', rol: 'instalador', horas_normales: '', horas_extra: '', monto_total: '', fecha: '', notas: '' });
      setDoc(null);
      onChanged();
    } catch (e) { toast.error('Error'); }
  };

  const remove = async (id) => { if (window.confirm('¿Eliminar?')) { await api.delete(`/produccion/ot/${ot.id}/mano_obra/${id}`); onChanged(); } };

  return (
    <div className="space-y-3">
      <div className="bg-[var(--surface-muted)] rounded-md p-3 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <input value={form.instalador_nombre} onChange={(e) => setForm({ ...form, instalador_nombre: e.target.value })} placeholder="Nombre instalador" className="px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs" />
          <input value={form.rol} onChange={(e) => setForm({ ...form, rol: e.target.value })} placeholder="Rol (instalador/ayudante)" className="px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs" />
        </div>
        <div className="grid grid-cols-4 gap-2">
          <input type="number" value={form.horas_normales} onChange={(e) => setForm({ ...form, horas_normales: e.target.value })} placeholder="Hrs normales" className="px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs" />
          <input type="number" value={form.horas_extra} onChange={(e) => setForm({ ...form, horas_extra: e.target.value })} placeholder="Hrs extra" className="px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs" />
          <input type="number" value={form.monto_total} onChange={(e) => setForm({ ...form, monto_total: e.target.value })} placeholder="Monto Q" className="px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs" />
          <input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} className="px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs" />
        </div>
        <input type="file" disabled={uploading} onChange={async (e) => { const r = await upload(e.target.files?.[0]); if (r) setDoc(r); }} className="text-xs" />
        {doc && <p className="text-[10px] text-emerald-400">📎 {doc.nombre}</p>}
        <button onClick={add} className="w-full px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-xs font-bold">+ Agregar instalador / pago</button>
      </div>

      <table className="w-full text-xs">
        <thead><tr className="text-[10px] uppercase text-[var(--text-muted)] border-b border-[var(--border)]">
          <th className="text-left py-1">Instalador</th><th className="py-1">Rol</th><th className="text-right py-1">Hrs/Ext</th><th className="text-right py-1">Monto</th><th></th><th></th>
        </tr></thead>
        <tbody>{(ot.mano_obra || []).map(m => (
          <tr key={m.id} className="border-b border-[var(--border)]/40">
            <td className="py-1">{m.instalador_nombre}</td>
            <td className="py-1 text-[var(--text-muted)]">{m.rol || '—'}</td>
            <td className="py-1 text-right">{m.horas_normales || 0}/{m.horas_extra || 0}</td>
            <td className="py-1 text-right font-bold text-emerald-400">{fmtQ(m.monto_total)}</td>
            <td className="py-1 text-center">{m.documento_url && <a href={m.documento_url} target="_blank" rel="noreferrer">📎</a>}</td>
            <td className="py-1 text-right"><button onClick={() => remove(m.id)} className="text-red-400"><Trash size={11} /></button></td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
};

const TabGastos = ({ ot, onChanged, canAdminCosts }) => {
  const [mode, setMode] = useState('operativo'); // operativo | admin
  const [opForm, setOpForm] = useState({ tipo: 'flete', descripcion: '', monto: '', fecha: '', proveedor: '', factura_numero: '' });
  const [opSinRespaldo, setOpSinRespaldo] = useState(false);
  const [opSinMotivo, setOpSinMotivo] = useState('');
  const [adForm, setAdForm] = useState({ categoria: 'sueldo_admin', descripcion: '', monto: '', fecha: '', persona: '' });
  const [adSinRespaldo, setAdSinRespaldo] = useState(false);
  const [adSinMotivo, setAdSinMotivo] = useState('');
  const [doc, setDoc] = useState(null);
  const { upload, uploading } = useUpload();

  const addOp = async () => {
    if (!opForm.monto || !opForm.descripcion) return toast.error('Datos requeridos');
    try {
      await api.post(`/produccion/ot/${ot.id}/gasto`, {
        ...opForm,
        monto: Number(opForm.monto || 0),
        fecha: opForm.fecha || null,
        documento_url: doc?.url || null,
        documento_nombre: doc?.nombre || null,
        sin_respaldo: opSinRespaldo,
        sin_respaldo_motivo: opSinRespaldo ? opSinMotivo : null,
      });
      toast.success('Gasto registrado');
      setOpForm({ tipo: 'flete', descripcion: '', monto: '', fecha: '', proveedor: '', factura_numero: '' });
      setDoc(null); setOpSinRespaldo(false); setOpSinMotivo('');
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const addAd = async () => {
    if (!adForm.monto || !adForm.descripcion) return toast.error('Datos requeridos');
    try {
      await api.post(`/produccion/ot/${ot.id}/gasto-admin`, {
        ...adForm,
        monto: Number(adForm.monto || 0),
        fecha: adForm.fecha || null,
        sin_respaldo: adSinRespaldo,
        sin_respaldo_motivo: adSinRespaldo ? adSinMotivo : null,
      });
      toast.success('Costo admin registrado');
      setAdForm({ categoria: 'sueldo_admin', descripcion: '', monto: '', fecha: '', persona: '' });
      setAdSinRespaldo(false); setAdSinMotivo('');
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const removeOp = async (id) => { if (window.confirm('¿Eliminar?')) { await api.delete(`/produccion/ot/${ot.id}/gastos/${id}`); onChanged(); } };
  const removeAd = async (id) => { if (window.confirm('¿Eliminar?')) { await api.delete(`/produccion/ot/${ot.id}/gastos_admin/${id}`); onChanged(); } };

  return (
    <div className="space-y-3">
      <div className="flex gap-1.5">
        <button onClick={() => setMode('operativo')} className={`px-3 py-1.5 text-xs font-bold rounded-md ${mode === 'operativo' ? 'bg-orange-600 text-white' : 'bg-[var(--surface-muted)]'}`}>Operativos</button>
        {canAdminCosts && <button onClick={() => setMode('admin')} className={`px-3 py-1.5 text-xs font-bold rounded-md ${mode === 'admin' ? 'bg-purple-600 text-white' : 'bg-[var(--surface-muted)]'}`}>Administrativos (financiero)</button>}
      </div>

      {mode === 'operativo' && (
        <>
          <div className="bg-[var(--surface-muted)] rounded-md p-3 space-y-2">
            <div className="grid grid-cols-3 gap-2">
              <select value={opForm.tipo} onChange={(e) => setOpForm({ ...opForm, tipo: e.target.value })} className="px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs">
                {['flete', 'viatico', 'insumos', 'error_recompra', 'horas_extra', 'herramienta', 'otro'].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <input type="number" value={opForm.monto} onChange={(e) => setOpForm({ ...opForm, monto: e.target.value })} placeholder="Monto" className="px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs" />
              <input type="date" value={opForm.fecha} onChange={(e) => setOpForm({ ...opForm, fecha: e.target.value })} className="px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs" />
            </div>
            <input value={opForm.descripcion} onChange={(e) => setOpForm({ ...opForm, descripcion: e.target.value })} placeholder="Descripción" className="w-full px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs" />
            <div className="grid grid-cols-2 gap-2">
              <input value={opForm.proveedor} onChange={(e) => setOpForm({ ...opForm, proveedor: e.target.value })} placeholder="Proveedor" className="px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs" />
              <input value={opForm.factura_numero} onChange={(e) => setOpForm({ ...opForm, factura_numero: e.target.value })} placeholder="N° Factura" className="px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs" />
            </div>
            <input type="file" disabled={uploading} onChange={async (e) => { const r = await upload(e.target.files?.[0]); if (r) setDoc(r); }} className="text-xs" />
            {doc && <p className="text-[10px] text-emerald-400">📎 {doc.nombre}</p>}
            <div className="bg-amber-500/20 border border-amber-500/50 rounded p-2">
              <label className="flex items-center gap-1.5 text-[12px] text-amber-900 dark:text-amber-100 cursor-pointer">
                <input type="checkbox" checked={opSinRespaldo} onChange={(e) => setOpSinRespaldo(e.target.checked)} data-testid="check-sin-respaldo-op" />
                <span><b>Registrar gasto sin documento de respaldo</b> <span className="text-amber-800 dark:text-amber-200">(queda flag de auditoría)</span></span>
              </label>
              {opSinRespaldo && (
                <input value={opSinMotivo} onChange={(e) => setOpSinMotivo(e.target.value)} placeholder="Motivo (≥5 caracteres)" className="mt-1.5 w-full px-2 py-1 bg-[var(--surface)] border border-amber-500/60 rounded text-xs" />
              )}
            </div>
            <button onClick={addOp} className="w-full px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-xs font-bold">+ Agregar gasto</button>
          </div>
          <table className="w-full text-xs">
            <thead><tr className="text-[10px] uppercase text-[var(--text-muted)] border-b border-[var(--border)]">
              <th className="text-left py-1">Tipo</th><th className="text-left py-1">Descripción</th><th className="text-right py-1">Monto</th><th></th><th></th>
            </tr></thead>
            <tbody>{(ot.gastos || []).map(g => (
              <tr key={g.id} className={`border-b border-[var(--border)]/40 ${g.sin_respaldo ? 'bg-amber-500/5' : ''}`}>
                <td className="py-1 capitalize">
                  {g.tipo}
                  {g.sin_respaldo && <span title={g.sin_respaldo_motivo} className="ml-1 text-[9px] bg-amber-500/30 text-amber-800 dark:text-amber-200 px-1 rounded">⚠ sin respaldo</span>}
                </td>
                <td className="py-1">{g.descripcion}</td>
                <td className="py-1 text-right font-bold text-red-300">{fmtQ(g.monto)}</td>
                <td className="py-1 text-center">{g.documento_url && <a href={g.documento_url} target="_blank" rel="noreferrer">📎</a>}</td>
                <td className="py-1 text-right"><button onClick={() => removeOp(g.id)} className="text-red-400"><Trash size={11} /></button></td>
              </tr>
            ))}</tbody>
          </table>
        </>
      )}

      {mode === 'admin' && canAdminCosts && (
        <>
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-md p-3 space-y-2">
            <p className="text-[11px] text-purple-200 font-bold">🔒 Costos administrativos (solo financiero/admin)</p>
            <div className="grid grid-cols-3 gap-2">
              <select value={adForm.categoria} onChange={(e) => setAdForm({ ...adForm, categoria: e.target.value })} className="px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs">
                <option value="sueldo_admin">Sueldo admin</option>
                <option value="comision_vendedor">Comisión vendedor</option>
                <option value="comision_gerente">Comisión gerente</option>
                <option value="otro">Otro</option>
              </select>
              <input type="number" value={adForm.monto} onChange={(e) => setAdForm({ ...adForm, monto: e.target.value })} placeholder="Monto" className="px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs" />
              <input type="date" value={adForm.fecha} onChange={(e) => setAdForm({ ...adForm, fecha: e.target.value })} className="px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs" />
            </div>
            <input value={adForm.descripcion} onChange={(e) => setAdForm({ ...adForm, descripcion: e.target.value })} placeholder="Descripción" className="w-full px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs" />
            <input value={adForm.persona} onChange={(e) => setAdForm({ ...adForm, persona: e.target.value })} placeholder="Beneficiario" className="w-full px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs" />
            <div className="bg-amber-500/20 border border-amber-500/50 rounded p-2">
              <label className="flex items-center gap-1.5 text-[12px] text-amber-900 dark:text-amber-100 cursor-pointer">
                <input type="checkbox" checked={adSinRespaldo} onChange={(e) => setAdSinRespaldo(e.target.checked)} data-testid="check-sin-respaldo-admin" />
                <span><b>Registrar costo admin sin respaldo</b> <span className="text-amber-800 dark:text-amber-200">(queda flag de auditoría)</span></span>
              </label>
              {adSinRespaldo && (
                <input value={adSinMotivo} onChange={(e) => setAdSinMotivo(e.target.value)} placeholder="Motivo (≥5 caracteres)" className="mt-1.5 w-full px-2 py-1 bg-[var(--surface)] border border-amber-500/60 rounded text-xs" />
              )}
            </div>
            <button onClick={addAd} className="w-full px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md text-xs font-bold">+ Agregar costo admin</button>
          </div>
          <table className="w-full text-xs">
            <thead><tr className="text-[10px] uppercase text-[var(--text-muted)] border-b border-[var(--border)]">
              <th className="text-left py-1">Categoría</th><th className="text-left py-1">Descripción</th><th className="text-left py-1">Persona</th><th className="text-right py-1">Monto</th><th></th>
            </tr></thead>
            <tbody>{(ot.gastos_admin || []).map(g => (
              <tr key={g.id} className={`border-b border-[var(--border)]/40 ${g.sin_respaldo ? 'bg-amber-500/5' : ''}`}>
                <td className="py-1 capitalize">
                  {g.categoria?.replace(/_/g, ' ')}
                  {g.sin_respaldo && <span title={g.sin_respaldo_motivo} className="ml-1 text-[9px] bg-amber-500/30 text-amber-800 dark:text-amber-200 px-1 rounded">⚠ sin respaldo</span>}
                </td>
                <td className="py-1">{g.descripcion}</td>
                <td className="py-1 text-[var(--text-muted)]">{g.persona || '—'}</td>
                <td className="py-1 text-right font-bold text-purple-300">{fmtQ(g.monto)}</td>
                <td className="py-1 text-right"><button onClick={() => removeAd(g.id)} className="text-red-400"><Trash size={11} /></button></td>
              </tr>
            ))}</tbody>
          </table>
        </>
      )}
    </div>
  );
};

const TabDocumentos = ({ ot, onChanged }) => {
  const [cat, setCat] = useState('foto_obra');
  const [doc, setDoc] = useState(null);
  const { upload, uploading } = useUpload();

  const add = async () => {
    if (!doc) return toast.error('Sube un archivo');
    try {
      await api.post(`/produccion/ot/${ot.id}/documento`, { nombre: doc.nombre, url: doc.url, categoria: cat });
      toast.success('Documento agregado');
      setDoc(null);
      onChanged();
    } catch (e) { toast.error('Error'); }
  };

  const remove = async (id) => { if (window.confirm('¿Eliminar?')) { await api.delete(`/produccion/ot/${ot.id}/documentos/${id}`); onChanged(); } };

  return (
    <div className="space-y-3">
      <div className="bg-[var(--surface-muted)] rounded-md p-3 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <select value={cat} onChange={(e) => setCat(e.target.value)} className="px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs">
            <option value="factura">Factura</option>
            <option value="recibo">Recibo</option>
            <option value="dibujo">Dibujo / diseño</option>
            <option value="cotizacion">Cotización</option>
            <option value="foto_obra">Foto obra (antes/durante)</option>
            <option value="otro">Otro</option>
          </select>
          <input type="file" disabled={uploading} onChange={async (e) => { const r = await upload(e.target.files?.[0]); if (r) setDoc(r); }} className="text-xs" />
        </div>
        {doc && <p className="text-[10px] text-emerald-400">📎 {doc.nombre}</p>}
        <button onClick={add} disabled={!doc} className="w-full px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-xs font-bold disabled:opacity-50">+ Adjuntar</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {(ot.documentos || []).map(d => (
          <div key={d.id} className="bg-[var(--surface-muted)] rounded-md p-2 group relative">
            <a href={d.url} target="_blank" rel="noreferrer" className="text-xs font-bold text-blue-300 hover:underline block truncate">📎 {d.nombre}</a>
            <p className="text-[10px] text-[var(--text-faint)] mt-0.5">{d.categoria}</p>
            <button onClick={() => remove(d.id)} className="absolute top-1 right-1 text-red-400 opacity-0 group-hover:opacity-100"><Trash size={11} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const TabTimeline = ({ ot, onChanged }) => {
  const [text, setText] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);

  const addComment = async () => {
    if (!text.trim()) return;
    try {
      await api.post(`/produccion/ot/${ot.id}/comentario`, { texto: text });
      setText('');
      onChanged();
    } catch (e) { toast.error('Error'); }
  };

  const events = [
    ...(ot.timeline || []).map(t => ({ ...t, kind: 'event' })),
    ...(ot.comentarios || []).map(c => ({ ...c, kind: 'comment', at: c.created_at, by: c.created_by_name, evento: c.texto })),
  ].sort((a, b) => new Date(b.at) - new Date(a.at));

  // ▸ Etapas para la barra horizontal (en orden de progreso)
  const STAGES_FULL = [
    { key: 'abierta',     label: 'Abierta',     color: '#3B82F6' },
    { key: 'en_proceso',  label: 'En proceso',  color: '#F59E0B' },
    { key: 'instalando',  label: 'Instalando',  color: '#A855F7' },
    { key: 'entregada',   label: 'Entregada',   color: '#10B981' },
    { key: 'cerrada',     label: 'Cerrada',     color: '#64748B' },
  ];
  // Si es solo suministro, omitir la etapa "Instalando"
  const STAGES = ot.es_suministro ? STAGES_FULL.filter(s => s.key !== 'instalando') : STAGES_FULL;
  const isCancelled = ot.estado === 'cancelada';
  const currentIdx = isCancelled ? -1 : Math.max(0, STAGES.findIndex(s => s.key === ot.estado));

  // Fecha por etapa: buscar la primera marca de timeline que cambió a ese estado
  const stageDates = {};
  (ot.timeline || []).forEach(t => {
    const ev = (t.evento || '').toLowerCase();
    STAGES.forEach(s => {
      if (!stageDates[s.key] && (ev.includes(`estado: ${s.key}`) || ev.includes(`-> ${s.key}`) || ev.includes(`a ${s.key}`))) {
        stageDates[s.key] = t.at;
      }
    });
  });
  if (!stageDates.abierta) stageDates.abierta = ot.created_at || ot.fecha_creacion;
  if (!stageDates.entregada) stageDates.entregada = ot.fecha_entregada;

  // ▸ Línea de tiempo cronológica de eventos (puntos en el eje X)
  const evAsc = [...events].sort((a, b) => new Date(a.at) - new Date(b.at));
  const tMin = evAsc[0] ? new Date(evAsc[0].at).getTime() : Date.now();
  const tMax = evAsc[evAsc.length - 1] ? new Date(evAsc[evAsc.length - 1].at).getTime() : tMin + 1;
  const tSpan = Math.max(tMax - tMin, 1);
  const fmtShort = (s) => s ? new Date(s).toLocaleDateString('es-GT', { day: '2-digit', month: 'short' }) : '—';

  return (
    <div className="space-y-4">
      {/* ▸ Barra horizontal de etapas */}
      <div className="bg-[var(--surface-muted)] rounded-md p-4">
        <p className="text-[10px] uppercase font-bold text-[var(--text-muted)] tracking-wider mb-3">Progreso de la OT</p>
        {isCancelled ? (
          <div className="text-center py-2">
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-red-500/15 border border-red-500/40 text-red-700 dark:text-red-300">❌ OT Cancelada</span>
          </div>
        ) : (
          <div className="relative" data-testid="timeline-stages">
            <div className="flex items-start justify-between relative">
              {/* Línea conectora */}
              <div className="absolute top-3 left-3 right-3 h-0.5 bg-[var(--border)] -z-0" />
              <div
                className="absolute top-3 left-3 h-0.5 bg-gradient-to-r from-emerald-500 to-blue-500 -z-0 transition-all"
                style={{ width: `calc(${(currentIdx / (STAGES.length - 1)) * 100}% * ${(STAGES.length - 1) / STAGES.length} + 0px)` }}
              />
              {STAGES.map((s, idx) => {
                const completed = idx < currentIdx;
                const active = idx === currentIdx;
                const pending = idx > currentIdx;
                return (
                  <div key={s.key} className="flex flex-col items-center gap-1.5 z-10 flex-1" data-testid={`stage-${s.key}`}>
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black border-2 transition ${
                        completed ? 'bg-emerald-500 border-emerald-500 text-white' :
                        active ? 'border-4 text-white shadow-lg' :
                        'bg-[var(--surface)] border-[var(--border)] text-[var(--text-muted)]'
                      }`}
                      style={active ? { background: s.color, borderColor: s.color, boxShadow: `0 0 0 4px ${s.color}33` } : {}}
                    >
                      {completed ? '✓' : idx + 1}
                    </div>
                    <p className={`text-[10px] font-bold text-center leading-tight ${active ? 'text-[var(--text)]' : pending ? 'text-[var(--text-muted)]' : 'text-[var(--text)]'}`}>
                      {s.label}
                    </p>
                    <p className="text-[9px] text-[var(--text-faint)] text-center">
                      {fmtShort(stageDates[s.key])}
                    </p>
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4 text-[11px]">
              <div className="bg-[var(--surface)] rounded-md p-2">
                <p className="text-[9px] uppercase text-[var(--text-muted)] font-bold">Inicio estimado</p>
                <p className="font-bold text-[var(--text)]">{fmtDate(ot.fecha_inicio_estimada)}</p>
              </div>
              <div className="bg-[var(--surface)] rounded-md p-2">
                <p className="text-[9px] uppercase text-[var(--text-muted)] font-bold">Entrega estimada</p>
                <p className="font-bold text-[var(--text)]">{fmtDate(ot.fecha_entrega_estimada)}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ▸ Línea horizontal de eventos / avances (puntos clickables) */}
      {evAsc.length > 0 && (
        <div className="bg-[var(--surface-muted)] rounded-md p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] uppercase font-bold text-[var(--text-muted)] tracking-wider">Línea de tiempo · {evAsc.length} evento(s) · {ot.es_suministro && <span className="text-blue-600">solo suministro</span>}</p>
            <p className="text-[10px] text-[var(--text-muted)]">{fmtShort(evAsc[0].at)} → {fmtShort(evAsc[evAsc.length - 1].at)}</p>
          </div>
          <div className="relative h-16" data-testid="timeline-events-bar">
            {/* Eje */}
            <div className="absolute top-1/2 left-2 right-2 h-0.5 bg-gradient-to-r from-[var(--border)] via-blue-500/40 to-[var(--border)]" />
            {/* Puntos clickables */}
            {evAsc.map((e, i) => {
              const left = ((new Date(e.at).getTime() - tMin) / tSpan) * 100;
              const isComment = e.kind === 'comment';
              const isSelected = selectedEvent === i;
              return (
                <button
                  key={i}
                  onClick={() => setSelectedEvent(isSelected ? null : i)}
                  className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 group"
                  style={{ left: `calc(${left}% + 8px - ${left * 0.16}px)` }}
                  data-testid={`timeline-event-dot-${i}`}
                  title="Clic para ver detalle"
                >
                  <div className={`w-4 h-4 rounded-full border-2 border-[var(--surface)] transition-transform hover:scale-150 ${isComment ? 'bg-blue-500' : 'bg-emerald-500'} ${isSelected ? 'scale-150 ring-2 ring-orange-500' : ''}`} />
                </button>
              );
            })}
          </div>
          <div className="flex gap-3 text-[10px] text-[var(--text-muted)] mt-1">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Evento del sistema</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> Comentario</span>
            <span className="ml-auto italic">clic en un punto para ver detalle</span>
          </div>
          {/* Detalle del evento seleccionado */}
          {selectedEvent !== null && evAsc[selectedEvent] && (
            <div className="mt-3 p-3 rounded-md border border-orange-500/40 bg-orange-500/5" data-testid="timeline-event-detail">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[9px] uppercase font-bold px-2 py-0.5 rounded ${evAsc[selectedEvent].kind === 'comment' ? 'bg-blue-500/20 text-blue-700 dark:text-blue-300' : 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300'}`}>
                      {evAsc[selectedEvent].kind === 'comment' ? 'Comentario' : 'Evento'}
                    </span>
                    <p className="text-xs font-black text-[var(--text)]">{evAsc[selectedEvent].by || '—'}</p>
                  </div>
                  <p className="text-[11px] text-[var(--text-muted)] mt-0.5">{new Date(evAsc[selectedEvent].at).toLocaleString('es-GT')}</p>
                  <p className="text-sm text-[var(--text)] mt-1">{evAsc[selectedEvent].evento}</p>
                </div>
                <button onClick={() => setSelectedEvent(null)} className="text-[var(--text-muted)] hover:text-[var(--text)]"><X size={14} /></button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ▸ Agregar comentario */}
      <div className="bg-[var(--surface-muted)] rounded-md p-3 space-y-2">
        <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Agregar comentario al timeline…" className="w-full px-2 py-1.5 bg-[var(--surface)] border border-[var(--border)] rounded text-xs" data-testid="timeline-comment-input" />
        <button onClick={addComment} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-xs font-bold" data-testid="timeline-comment-btn">Comentar</button>
      </div>

      {/* ▸ Detalle vertical (lista cronológica completa) */}
      <div className="space-y-2">
        <p className="text-[10px] uppercase font-bold text-[var(--text-muted)] tracking-wider">Detalle cronológico</p>
        {events.map((e, i) => (
          <div key={i} className={`p-2.5 rounded-md border ${e.kind === 'comment' ? 'border-blue-500/30 bg-blue-500/5' : 'border-[var(--border)] bg-[var(--surface-muted)]'}`}>
            <p className="text-xs"><b>{e.by}</b> · <span className="text-[var(--text-faint)]">{new Date(e.at).toLocaleString('es-GT')}</span></p>
            <p className="text-xs text-[var(--text)] mt-0.5">{e.evento}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const TabRentabilidad = ({ r, ot }) => {
  const kpi = (label, value, color = 'text-[var(--text)]') => (
    <div className="bg-[var(--surface-muted)] rounded-md p-3">
      <p className="text-[10px] uppercase font-bold text-[var(--text-muted)]">{label}</p>
      <p className={`text-lg font-black ${color}`}>{value}</p>
    </div>
  );
  const docsCot = (ot?.docs_cotizados || []).reduce((s, d) => s + Number(d.valor || 0), 0);
  const comprado = (ot?.gastos || []).reduce((s, g) => s + Number(g.monto || 0), 0)
    + (ot?.materiales || []).filter((m) => m.fase === 'comprado').reduce((s, m) => s + Number(m.subtotal || 0), 0);
  const difDocs = docsCot - comprado;
  const pctUso = docsCot > 0 ? (comprado / docsCot) * 100 : 0;
  return (
    <div className="space-y-3">
      {docsCot > 0 && (
        <div className={`border-2 rounded-md p-3 ${difDocs >= 0 ? (pctUso >= 90 ? 'bg-amber-500/10 border-amber-500/40' : 'bg-emerald-500/10 border-emerald-500/40') : 'bg-red-500/10 border-red-500/40'}`}
          data-testid="semaforo-cotizado-comprado">
          <div className="flex items-center gap-2 flex-wrap justify-between">
            <p className="text-xs font-black">
              {difDocs >= 0 ? (pctUso >= 90 ? '🟡' : '🟢') : '🔴'} Cotizado vs Comprado:
              <span className="ml-1 font-bold">{fmtQ(docsCot)} cotizado · {fmtQ(comprado)} comprado ({pctUso.toFixed(0)}%)</span>
            </p>
            <p className={`text-xs font-black ${difDocs >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
              {difDocs >= 0 ? `Margen disponible: ${fmtQ(difDocs)}` : `⚠️ Excedido en ${fmtQ(-difDocs)}`}
            </p>
          </div>
          <div className="mt-2 h-2 bg-[var(--surface-muted)] rounded-full overflow-hidden">
            <div className={`h-full ${difDocs >= 0 ? (pctUso >= 90 ? 'bg-amber-500' : 'bg-emerald-500') : 'bg-red-500'}`}
              style={{ width: `${Math.min(100, pctUso)}%` }} />
          </div>
          <p className="text-[9px] text-[var(--text-muted)] mt-1">Base: documentos de lo cotizado (pestaña Materiales) vs gastos + materiales comprados.</p>
        </div>
      )}
      {(r.sin_respaldo_count || 0) > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-md p-3" data-testid="audit-warning">
          <p className="text-xs font-bold text-amber-800 dark:text-amber-200">⚠ Auditoría: {r.sin_respaldo_count} gasto(s) sin documento de respaldo · total {fmtQ(r.sin_respaldo_total)}</p>
          <p className="text-[10px] text-amber-800 dark:text-amber-100/80 mt-1">Estos montos están incluidos en el cálculo pero quedan flagueados para revisión contable.</p>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {kpi('Cotizado', fmtQ(r.cotizado_total), 'text-blue-300')}
        {kpi('Ingresos', fmtQ(r.ingresos), 'text-emerald-400')}
        {kpi('Saldo cliente', fmtQ(r.saldo_pendiente_cliente), 'text-amber-700 dark:text-amber-300')}
        {kpi('Material cotizado', fmtQ(r.material_cotizado))}
        {kpi('Material comprado', fmtQ(r.material_comprado))}
        {kpi('Desviación', `${fmtQ(r.desviacion_material)} (${r.desviacion_material_pct?.toFixed?.(1)}%)`, r.desviacion_material > 0 ? 'text-red-300' : 'text-emerald-300')}
        {kpi('Mano obra', fmtQ(r.mano_obra_total))}
        {kpi('Fletes + viáticos', fmtQ((r.fletes || 0) + (r.viaticos || 0)))}
        {kpi('Errores', fmtQ(r.errores), 'text-red-300')}
        {kpi('Costo directo', fmtQ(r.costo_total_directo), 'text-orange-300')}
        {kpi('Costos admin', fmtQ(r.admin_total), 'text-purple-300')}
        {kpi('Costo total c/admin', fmtQ(r.costo_total_con_admin), 'text-orange-300')}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {kpi('Utilidad bruta', fmtQ(r.utilidad_bruta), (r.utilidad_bruta || 0) >= 0 ? 'text-emerald-400' : 'text-red-400')}
        {kpi('Margen bruto %', `${(r.margen_bruto_pct || 0).toFixed(2)}%`, (r.margen_bruto_pct || 0) >= 20 ? 'text-emerald-400' : (r.margen_bruto_pct || 0) >= 10 ? 'text-amber-700 dark:text-amber-300' : 'text-red-300')}
        {kpi('Utilidad NETA', fmtQ(r.utilidad_neta), (r.utilidad_neta || 0) >= 0 ? 'text-emerald-400' : 'text-red-400')}
        {kpi('Margen NETO %', `${(r.margen_neto_pct || 0).toFixed(2)}%`, (r.margen_neto_pct || 0) >= 20 ? 'text-emerald-400' : (r.margen_neto_pct || 0) >= 10 ? 'text-amber-700 dark:text-amber-300' : 'text-red-300')}
      </div>
    </div>
  );
};

export default Produccion;
